<template>
  <!-- Wrap in div to avoid transition warning on  renders non-element root node that cannot be animated.  -->
  <div>
    <VOnboardingWrapper
      ref="wrapper"
      :steps="onboardingSteps"
      :options="wrapperOptions"
    >
      <template #default="{ previous, next, step, isFirst, isLast, index }">
        <VOnboardingStep>
          <q-card
            class="q-pa-md onboarding-tooltip q-mt-md"
            style="max-width: 420px;"
          >
            <div class="onboarding-tooltip-arrow" />
            <q-card-section class="q-pa-none">
              <div v-if="step.content">
                <h3
                  v-if="step.content.title"
                  class="text-h6"
                >
                  {{ step.content.title }}
                </h3>
                <div
                  v-if="step.content.description"
                  class="q-mt-sm"
                >
                  <p class="text-caption">
                    {{ step.content.description }}
                  </p>
                </div>
              </div>
            </q-card-section>
            <q-card-actions align="right">
              <span class="absolute right-0 bottom-full q-mb-sm q-mr-sm text-caption">{{ `${index + 1}/${onboardingSteps.length}` }}</span>
              <template v-if="!isFirst">
                <q-btn
                  dense
                  outline
                  icon="chevron_left"
                  @click="previous"
                />
              </template>
              <template v-if="isLast">
                <q-btn
                  dense
                  :label="$q.lang.label.ok"
                  color="primary"
                  unelevated
                  @click="handleOnboardingFinish"
                />
              </template>
              <template v-else>
                <q-btn
                  dense
                  color="primary"
                  unelevated
                  icon-right="chevron_right"
                  @click="next"
                />
              </template>
            </q-card-actions>
          </q-card>
        </VOnboardingStep>
      </template>
    </VOnboardingWrapper>


    <q-page class="q-px-xl q-pt-xl">
      <div class="fit row wrap justify-between q-mb-md items-center">
        <div class="col-auto row items-center">
          <span class="text-h4">{{ i18n.page_title }}</span>
        </div>

        <!-- Top Right Actions -->
        <div class="col-auto row items-center">
          <!-- Export menu -->
          <q-btn
            flat
            dense
            round
            icon="sym_o_download"
            class="q-mr-sm"
          >
            <q-tooltip
              anchor="center left"
              self="center right"
            >
              {{ i18n.actions_export_dropdown_label }}
            </q-tooltip>
            <q-menu
              anchor="bottom right"
              self="top right"
            >
              <q-list>
                <q-item
                  v-close-popup
                  clickable
                  @click="supporting_documents_download"
                >
                  <q-item-section avatar>
                    <q-icon name="sym_o_attachment" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ i18n.actions_export_justificatifs }}</q-item-label>
                  </q-item-section>
                </q-item>
                <q-item
                  v-close-popup
                  clickable
                  tag="a"
                  :href="transactions_export_path"
                  target="_blank"
                  download
                >
                  <q-item-section avatar>
                    <q-icon name="sym_o_table" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ i18n.actions_export_csv_short }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>

          <q-btn
            id="tooltip-settings"
            flat
            dense
            icon="sym_o_settings"
            class="q-mr-md"
            round
            @click="$refs.transactionsSettingsDialog && $refs.transactionsSettingsDialog.transactions_settings_open()"
          >
            <q-tooltip>{{ i18n.actions_parameters_link_label }}</q-tooltip>
          </q-btn>

          <TransactionsSettingsDialog
            ref="transactionsSettingsDialog"
            :transactions_settings="transactions_settings"
            :transactions_settings_state="transactions_settings_state"
          />

          <!-- Add Transaction Action -->
          <div>
            <q-btn
              :label="$q.screen.gt.sm ? i18n.transactions_add_label : ''"
              color="primary"
              icon="las la-plus"
              :round="!$q.screen.gt.sm"
              unelevated
              :disable="transactions_filters.exchange_accounts.length == 0"
              @click="transactions_add_dialog_open"
            />

            <q-dialog
              v-model="transactions_add_dialog_show"
              persistent
            >
              <q-card
                style="min-width: 350px; max-width: 600px; width: 100%"
                class="q-pa-md"
              >
                <q-card-section class="row items-center q-pb-none">
                  <div class="text-h5 text-bold">
                    {{ i18n.transactions_add_dialog_title }}
                  </div>
                  <q-space />
                  <q-btn
                    v-close-popup
                    icon="close"
                    flat
                    round
                    dense
                  />
                </q-card-section>

                <q-card-section class="q-pt-md q-pa-md">
                  <q-form
                    class=""
                    @submit="transactions_add_submit"
                  >
                    <!-- Exchange Account Selector -->
                    <span class="text-input-label">
                      {{ i18n.transactions_add_form_exchange_account }}
                    </span>
                    <q-select
                      v-model="transactions_add_dialog_form.exchange_account"
                      class="full-width q-mt-sm"
                      outlined
                      dense
                      :options="transactions_filters.exchange_accounts"
                      option-value="id"
                      option-label="name"
                      :rules="[(val) => !!val || i18n.required_field]"
                    >
                      <template v-slot:option="scope">
                        <q-item v-bind="scope.itemProps">
                          <q-item-section avatar>
                            <q-avatar
                              size="16px"
                              square
                            >
                              <img
                                :src="scope.opt.logo_url"
                                :alt="scope.opt.name"
                              >
                            </q-avatar>
                          </q-item-section>
                          <q-item-section>
                            <q-item-label>
                              {{ scope.opt.name }}
                              <span class="text-caption text-muted">({{ scope.opt.exchange_label }})</span>
                            </q-item-label>
                          </q-item-section>
                        </q-item>
                      </template>
                      <template
                        v-if="transactions_add_dialog_form.exchange_account?.logo_url"
                        v-slot:prepend
                      >
                        <q-avatar
                          size="18px"
                          square
                        >
                          <img
                            :src="transactions_add_dialog_form.exchange_account?.logo_url"
                            :alt="transactions_add_dialog_form.exchange_account?.exchange_label"
                          >
                        </q-avatar>
                      </template>
                      <template v-slot:selected-item="scope">
                        {{ scope.opt.name }}
                        <span class="text-caption text-muted q-pl-sm">
                          ({{ scope.opt.exchange_label }})
                        </span>
                      </template>
                    </q-select>

                    <transition
                      appear
                      enter-active-class="animated fadeIn"
                      leave-active-class="animated fadeOut"
                      :duration="2000"
                    >
                      <div
                        v-if="transactions_add_dialog_form.exchange_account"
                        class="form-fields"
                      >
                        <!-- Transaction Type Cards ( in/out/swap )-->
                        <span class="text-input-label">
                          {{ i18n.transactions_add_form_tx_type }}
                        </span>

                        <div class="row q-col-gutter-md q-pt-sm q-mb-lg">
                          <div
                            v-for="type in transactions_add_dialog_form_types"
                            :key="type.value"
                            class="col-xs-4"
                          >
                            <q-card
                              class="cursor-pointer q-hoverable"
                              :class="{
                                'highlighted': transactions_add_dialog_form.type?.value == type.value,
                              }"
                              flat
                              bordered
                              outline
                              @click="transactions_add_dialog_form.type = type"
                            >
                              <span class="q-focus-helper" />
                              <q-card-section class="text-left q-pa-none">
                                <q-list
                                  class="q-mx-auto"
                                  style="max-width: fit-content"
                                >
                                  <q-item>
                                    <q-item-section
                                      style="max-width: fit-content"
                                      class="q-pr-xs"
                                    >
                                      <q-icon
                                        :name="type.icon"
                                        :color="type.color"
                                        size="18px"
                                      />
                                    </q-item-section>
                                    <q-item-section>
                                      <q-item-label class="q-pr-xs">
                                        {{ type.label }}
                                      </q-item-label>
                                    </q-item-section>
                                  </q-item>
                                </q-list>
                              </q-card-section>
                            </q-card>
                          </div>
                        </div>

                        <!-- Date and Time -->
                        <div class="row q-col-gutter-md">
                          <div class="col-md-6 col-xs-12">
                            <span class="text-input-label">
                              {{ i18n.transactions_add_form_date }}
                            </span>
                            <q-input
                              v-model="transactions_add_dialog_form.date"
                              class="full-width q-mt-sm"
                              outlined
                              dense
                              mask="date"
                              :rules="[
                                'date',
                                val => {
                                  const today = new Date();
                                  today.setHours(0, 0, 0, 0);
                                  const selectedDate = new Date(val);
                                  return selectedDate < today || i18n.transactions_add_form_date_invalid
                                }
                              ]"
                            >
                              <template v-slot:append>
                                <q-icon
                                  name="event"
                                  class="cursor-pointer"
                                >
                                  <q-popup-proxy
                                    cover
                                    transition-show="scale"
                                    transition-hide="scale"
                                  >
                                    <q-date
                                      v-model="transactions_add_dialog_form.date"
                                      minimal
                                      :options="date => {
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        return new Date(date) < today;
                                      }"
                                    >
                                      <div class="row items-center justify-end">
                                        <q-btn
                                          v-close-popup
                                          :label="$q.lang.label.close"
                                          color="primary"
                                          flat
                                        />
                                      </div>
                                    </q-date>
                                  </q-popup-proxy>
                                </q-icon>
                              </template>
                            </q-input>
                          </div>
                          <div class="col-md-6 col-xs-12">
                            <span class="text-input-label">
                              {{ i18n.transactions_add_form_time }}
                            </span>

                            <q-input
                              v-model="transactions_add_dialog_form.time"
                              class="full-width q-mt-sm"
                              outlined
                              dense
                              mask="fulltime"
                              :rules="['fulltime']"
                            >
                              <template v-slot:append>
                                <q-icon
                                  name="schedule"
                                  class="cursor-pointer"
                                >
                                  <q-popup-proxy
                                    cover
                                    transition-show="scale"
                                    transition-hide="scale"
                                  >
                                    <q-time
                                      v-model="transactions_add_dialog_form.time"
                                      with-seconds
                                      format24h
                                    >
                                      <div class="row items-center justify-end">
                                        <q-btn
                                          v-close-popup
                                          :label="$q.lang.label.close"
                                          color="primary"
                                          flat
                                        />
                                      </div>
                                    </q-time>
                                  </q-popup-proxy>
                                </q-icon>
                              </template>
                            </q-input>
                          </div>
                        </div>

                        <!-- Transaction IN fields (for credit and swap) -->
                        <div
                          v-if="['credit', 'swap'].includes(transactions_add_dialog_form.type?.value)"
                          class="row q-col-gutter-md"
                        >
                          <div class="col-md-6 col-xs-12">
                            <span class="text-input-label">
                              {{ i18n.transactions_add_form_amount }}
                              <template v-if="transactions_add_dialog_form.type?.value == 'swap'">
                                ({{ i18n.transactions_add_form_received }})
                              </template>
                            </span>
                            <q-input
                              v-model.number="transactions_add_dialog_form.transaction_in_amount"
                              class="full-width q-mt-sm"
                              outlined
                              dense
                              type="number"
                              step="any"
                              :rules="[(val) => !!val || i18n.required_field]"
                            />
                          </div>
                          <div class="col-md-6 col-xs-12">
                            <span class="text-input-label">
                              {{ i18n.transactions_add_form_asset }}
                              <template v-if="transactions_add_dialog_form.type?.value == 'swap'">
                                ({{ i18n.transactions_add_form_received }})
                              </template>
                            </span>
                            <q-btn-dropdown
                              align="between"
                              unelevated
                              class="full-width q-mt-sm"
                              outline
                              :label="transactions_add_dialog_form.transaction_in_asset?.exchange_symbol || i18n.transactions_add_form_asset"
                            >
                              <q-list
                                style="min-width: 240px"
                                class="q-pa-sm"
                                dense
                              >
                                <q-input
                                  :model-value="transactions_add_exchange_assets.search"
                                  outlined
                                  clearable
                                  :placeholder="$q.lang.label.search"
                                  dense
                                  class="q-pa-sm"
                                  autofocus
                                  debounce="500"
                                  @update:model-value="transactions_add_exchange_assets_search"
                                />

                                <!-- Empty state message -->
                                <q-item
                                  v-if="transactions_add_exchange_assets.exchange_assets.length === 0 && (!transactions_add_exchange_assets.search || transactions_add_exchange_assets.search.trim() === '')"
                                  class="text-center q-py-md"
                                >
                                  <q-item-section>
                                    <q-item-label class="text-grey-6">
                                      <div class="text-body2">
                                        <q-icon
                                          name="search"
                                          class="q-mr-sm"
                                        />
                                        {{ i18n.transactions_add_form_asset_search_empty }}
                                      </div>
                                      <div class="text-caption q-mt-xs">
                                        {{ i18n.transactions_add_form_asset_search_hint }}
                                      </div>
                                    </q-item-label>
                                  </q-item-section>
                                </q-item>

                                <q-item
                                  v-close-popup
                                  clickable
                                  dense
                                  @click="transactions_add_exchange_assets_dialog_open('in')"
                                >
                                  <q-item-section avatar>
                                    <q-icon
                                      name="add"
                                      color="primary"
                                    />
                                  </q-item-section>
                                  <q-item-section>
                                    <q-item-label class="text-primary">
                                      {{ i18n.transactions_add_dialog_asset_add_action }}
                                    </q-item-label>
                                  </q-item-section>
                                </q-item>

                                <q-separator />

                                <q-scroll-area style="height: 300px;">
                                  <q-item
                                    v-for="asset in transactions_add_exchange_assets.exchange_assets"
                                    :key="asset.id"
                                    v-close-popup
                                    clickable
                                    dense
                                    @click="transactions_add_dialog_form.transaction_in_asset = asset"
                                  >
                                    <q-item-section avatar>
                                      <!-- Asset Icon -->
                                      <img
                                        width="24px"
                                        height="24px"
                                        class="inline q-mr-xs"
                                        :alt="asset.name"
                                        :src="asset.logo_url"
                                      >
                                    </q-item-section>
                                    <q-item-section>
                                      <q-item-label>{{ asset.exchange_symbol }}</q-item-label>
                                    </q-item-section>
                                  </q-item>
                                </q-scroll-area>
                              </q-list>
                            </q-btn-dropdown>
                            <div
                              v-if="transactions_add_dialog_form_transaction_in_asset_error"
                              class="text-negative text-caption q-mt-xs"
                            >
                              {{ i18n.required_field }}
                            </div>
                          </div>
                        </div>

                        <!-- Transaction OUT fields (for debit and swap) -->
                        <div
                          v-if="['debit', 'swap'].includes(transactions_add_dialog_form.type?.value)"
                          class="row q-col-gutter-md"
                        >
                          <div class="col-md-6 col-xs-12">
                            <span class="text-input-label">
                              {{ i18n.transactions_add_form_amount }}
                              <template v-if="transactions_add_dialog_form.type?.value == 'swap'">
                                ({{ i18n.transactions_add_form_sent }})
                              </template>
                            </span>
                            <q-input
                              v-model.number="transactions_add_dialog_form.transaction_out_amount"
                              class="full-width q-mt-sm"
                              outlined
                              dense
                              type="number"
                              step="any"
                              :rules="[(val) => !!val || i18n.required_field]"
                            />
                          </div>
                          <div class="col-md-6 col-xs-12">
                            <span class="text-input-label">
                              {{ i18n.transactions_add_form_asset }}
                              <template v-if="transactions_add_dialog_form.type?.value == 'swap'">
                                ({{ i18n.transactions_add_form_sent }})
                              </template>
                            </span>
                            <q-btn-dropdown
                              align="between"
                              unelevated
                              class="full-width q-mt-sm"
                              outline
                              :label="transactions_add_dialog_form.transaction_out_asset?.exchange_symbol || i18n.transactions_add_form_asset"
                            >
                              <q-list
                                style="min-width: 240px"
                                class="q-pa-sm"
                                dense
                              >
                                <q-input
                                  :model-value="transactions_add_exchange_assets.search"
                                  outlined
                                  clearable
                                  :placeholder="$q.lang.label.search"
                                  dense
                                  class="q-pa-sm"
                                  autofocus
                                  debounce="500"
                                  @update:model-value="transactions_add_exchange_assets_search"
                                />

                                <!-- Empty state message -->
                                <q-item
                                  v-if="transactions_add_exchange_assets.exchange_assets.length === 0 && (!transactions_add_exchange_assets.search || transactions_add_exchange_assets.search.trim() === '')"
                                  class="text-center q-py-md"
                                >
                                  <q-item-section>
                                    <q-item-label class="text-grey-6">
                                      <div class="text-body2">
                                        <q-icon
                                          name="search"
                                          class="q-mr-sm"
                                        />
                                        {{ i18n.transactions_add_form_asset_search_empty }}
                                      </div>
                                      <div class="text-caption q-mt-xs">
                                        {{ i18n.transactions_add_form_asset_search_hint }}
                                      </div>
                                    </q-item-label>
                                  </q-item-section>
                                </q-item>

                                <q-item
                                  v-close-popup
                                  clickable
                                  dense
                                  @click="transactions_add_exchange_assets_dialog_open('out')"
                                >
                                  <q-item-section avatar>
                                    <q-icon
                                      name="add"
                                      color="primary"
                                    />
                                  </q-item-section>
                                  <q-item-section>
                                    <q-item-label class="text-primary">
                                      {{ i18n.transactions_add_dialog_asset_add_action }}
                                    </q-item-label>
                                  </q-item-section>
                                </q-item>

                                <q-separator />

                                <q-scroll-area style="height: 300px;">
                                  <q-item
                                    v-for="asset in transactions_add_exchange_assets.exchange_assets"
                                    :key="asset.id"
                                    v-close-popup
                                    clickable
                                    dense
                                    @click="transactions_add_dialog_form.transaction_out_asset = asset"
                                  >
                                    <q-item-section avatar>
                                      <!-- Asset Icon -->
                                      <img
                                        width="24px"
                                        height="24px"
                                        class="inline q-mr-xs"
                                        :alt="asset.name"
                                        :src="asset.logo_url"
                                      >
                                    </q-item-section>
                                    <q-item-section>
                                      <q-item-label>{{ asset.exchange_symbol }}</q-item-label>
                                    </q-item-section>
                                  </q-item>
                                </q-scroll-area>
                              </q-list>
                            </q-btn-dropdown>
                            <div
                              v-if="transactions_add_dialog_form_transaction_out_asset_error"
                              class="text-negative text-caption q-mt-xs"
                            >
                              {{ i18n.required_field }}
                            </div>
                          </div>
                        </div>

                        <!-- From/To addresses fields -->
                        <div
                          v-if="transactions_add_dialog_form.type?.value != 'swap'"
                          class="row q-col-gutter-md"
                        >
                          <div
                            v-if="transactions_add_dialog_form_sender_show"
                            class="col-md-6 col-xs-12"
                          >
                            <span class="text-input-label">
                              {{ i18n.transactions_add_form_sender }}
                            </span>
                            <q-input
                              v-model="transactions_add_dialog_form.from_address"
                              class="full-width q-mt-sm q-field--with-bottom"
                              outlined
                              dense
                            />
                          </div>
                          <div
                            v-if="transactions_add_dialog_form_recipient_show"
                            class="col-md-6 col-xs-12"
                          >
                            <span class="text-input-label">
                              {{ i18n.transactions_add_form_recipient }}
                            </span>
                            <q-input
                              v-model="transactions_add_dialog_form.to_address"
                              class="full-width q-mt-sm q-field--with-bottom"
                              outlined
                              dense
                            />
                          </div>
                        </div>

                        <!-- Optional fields toggles -->
                        <div
                          v-if="transactions_add_dialog_form.type?.value != 'swap'"
                          class="q-mt-md"
                        >
                          <q-chip
                            v-if="!transactions_add_dialog_form_sender_show"
                            clickable
                            icon="las la-plus"
                            color="primary"
                            text-color="primary"
                            outline
                            class="q-mr-sm"
                            @click="transactions_add_dialog_form_sender_show = true"
                          >
                            {{ i18n.transactions_add_form_sender }}
                          </q-chip>
                          <q-chip
                            v-if="!transactions_add_dialog_form_recipient_show"
                            clickable
                            icon="las la-plus"
                            color="primary"
                            text-color="primary"
                            outline
                            @click="transactions_add_dialog_form_recipient_show = true"
                          >
                            {{ i18n.transactions_add_form_recipient }}
                          </q-chip>
                        </div>

                        <q-banner
                          class="bg-pastel-blue q-pa-md q-mt-md"
                          dense
                        >
                          <template v-slot:avatar>
                            <q-icon
                              name="las la-info-circle"
                              class=""
                              size="lg"
                            />
                          </template>
                          {{ i18n.transactions_add_form_files_note }}
                        </q-banner>

                        <div class="row justify-end q-mt-md">
                          <q-btn
                            v-close-popup
                            :label="$q.lang.label.cancel"
                            flat
                            color="primary"
                            class="q-mr-sm"
                          />
                          <q-btn
                            :label="$q.lang.label.create"
                            type="submit"
                            color="positive"
                            :loading="transactions_add_dialog_loading"
                            unelevated
                          />
                        </div>
                      </div>
                    </transition>
                  </q-form>
                </q-card-section>

                <q-separator />

                <q-card-section class="row q-col-gutter-md text-center">
                  <!-- documentation-chip -->
                  <div class="col-12">
                    <span class="text-caption">{{ i18n.transactions_add_dialog_doc_label }}</span>
                    <q-chip
                      clickable
                      color="pastel-green"
                      size="12px"
                      class="q-ml-sm"
                      text-color="black"
                      icon="menu_book"
                      :label="i18n.transactions_add_dialog_doc_title"
                      @click="$intercom.article_open('11829622')"
                    />
                  </div>
                </q-card-section>
              </q-card>
            </q-dialog>

            <!-- ExchangeAsset Add -->
            <q-dialog
              v-model="transactions_assets_add_dialog_show"
              persistent
            >
              <q-card
                style="min-width: 480px"
                class="q-pa-md"
              >
                <q-card-section class="row items-center q-pb-none">
                  <div class="text-h6">
                    {{ i18n.transactions_assets_add_dialog_title.replace("%{0}", transactions_add_dialog_form.exchange_account?.exchange_label) }}
                  </div>
                  <q-space />
                  <q-btn
                    v-close-popup
                    icon="close"
                    flat
                    round
                    dense
                  />
                </q-card-section>

                <q-card-section>
                  <q-input
                    v-model="transactions_assets_add_form.exchange_symbol"
                    autofocus
                    :label="i18n.transactions_assets_add_form_symbol_placeholder"
                    outlined
                    dense
                    :rules="[(val) => !!val || i18n.required_field]"
                  />
                </q-card-section>

                <q-card-actions align="right">
                  <q-btn
                    v-close-popup
                    flat
                    :label="$q.lang.label.cancel"
                    color="primary"
                  />
                  <q-btn
                    :label="$q.lang.label.create"
                    color="positive"
                    :loading="transactions_assets_add_dialog_loading"
                    unelevated
                    @click="transactions_add_exchange_asset_create"
                  />
                </q-card-actions>
              </q-card>
            </q-dialog>
          </div>
        </div>
        <div class="col-12">
          <span
            v-if="transactions_counts.uncategorized != 0"
            id="tooltip-uncategorized"
            class="text-secondary"
            @click="transactions_filter_replace_all({ uncategorized: 'true' })"
            v-html="transactions_counts.uncategorized_text"
          />
        </div>
      </div>

      <template v-if="transactions_filters.exchange_accounts.length == 0">
        <!-- No Accounts -->
        <div class="fit row wrap">
          <div class="col-12 text-center q-mt-xl">
            <q-banner
              class="bg-pastel-green q-px-md"
              dense
              style="width: fit-content; margin: auto"
            >
              <template v-slot:avatar>
                <q-icon
                  name="las la-exclamation-triangle"
                  class="message-box-text"
                  size="md"
                />
              </template>
              <div class="">
                {{ i18n.exchange_accounts_empty }}
              </div>
            </q-banner>

            <Link :href="imports_page_action_path">
              <q-btn
                :label="i18n.exchange_accounts_add_action"
                color="primary"
                unelevated
                flat
                class="q-mt-md btn-underline"
                no-caps
              />
            </Link>
          </div>
        </div>
      </template>

      <template v-else>
        <TransactionsFilters
          :transactions_filters="transactions_filters"
          :workspace_settings="workspace_settings"
          :i18n="i18n"
          :state_search_params="state_search_params"
          :filters_clear_show="filters_clear_show"
          :filters_filled_count="filters_filled_count"
          :filters_exchange_accounts_selected="filters_exchange_accounts_selected"
          :filters_assets_selected="filters_assets_selected"
          :filters_kinds_selected="filters_kinds_selected"
          :filters_import_sources_selected="filters_import_sources_selected"
          :transactions_filter_add="transactions_filter_add"
          :transactions_filters_clear="transactions_filters_clear"
          :transactions_filter_uncategorized_toggle="transactions_filter_uncategorized_toggle"
          :create_categorization_rule_dialog_open="create_categorization_rule_dialog_open"
          :categorization_rules="categorization_rules"
          :categorization_rules_load="categorization_rules_load"
          :categorization_rules_apply_all="categorization_rules_apply_all"
          :categorization_rule_preview="categorization_rule_preview"
          :categorization_rule_apply="categorization_rule_apply"
          :categorization_rule_delete="categorization_rule_delete"
          :address_mappings_map="address_mappings_map"
          :address_mappings_dialog_open="address_mappings_dialog_open"
        />
      </template>

      <TransactionsTable
        :transactions="transactions"
        :state_transactions_meta="state_transactions_meta"
        :workspace_settings="workspace_settings"
        :i18n="i18n"
        :transactions_loading="transactions_loading"
        :state_search_params="state_search_params"
        :transactions_page_selected="transactions_page_selected"
        :transactions_all_pages_selected="transactions_all_pages_selected"
        :set_transactions_all_pages_selected="(val) => (transactions_all_pages_selected = val)"
        :transactions_selected_count="transactions_selected_count"
        :transactions_selection="transactions_selection"
        :transaction_details="transaction_details"
        :transactions_filter_add="transactions_filter_add"
        :transactions_page_select="transactions_page_select"
        :transactions_select="transactions_select"
        :transactions_details_sidebar_open="transactions_details_sidebar_open"
        :transactions_kind_dialog_open="transactions_kind_dialog_open"
        :format-amount="formatAmount"
        :copy-balance-to-clipboard="copyBalanceToClipboard"
        :address_mappings_map="address_mappings_map"
        :address_mappings_dialog_open="address_mappings_dialog_open"
        :missing_change_rate_html="missing_change_rate_html"
        :transactions_missing_change_rate_dialog_open="transactions_missing_change_rate_dialog_open"
        :filter_by_exact_date="filter_by_exact_date"
        :build-transaction-context-menu-items="buildTransactionContextMenuItems"
        :transactions_pagination_update="transactions_pagination_update"
      />

      <TransactionsBulkEdit
        :i18n="i18n"
        :transactions_selected_count="transactions_selected_count"
        :transactions_all_pages_selected="transactions_all_pages_selected"
        :transactions_count="transactions.meta.count"
        :bulk_edit_search="bulk_edit_search"
        :recent-transaction-kinds="recentTransactionKinds"
        :bulk_edit_categories="bulk_edit_categories"
        :bulk_edit_single_action="bulk_edit_single_action"
        :bulk_edit_action_description="bulk_edit_action_description"
        :bulk_edit_action_loading="bulk_edit_action_loading"
        :transactions_selected_sums="transactions_selected_sums"
        @update:bulk_edit_search="(v) => bulk_edit_search = v"
        @bulk_edit_close="bulk_edit_close"
        @bulk_edit_kind_select="bulk_edit_kind_select"
        @bulk_edit_transactions_merge="bulk_edit_transactions_merge"
        @bulk_edit_transactions_internal_transfer="bulk_edit_transactions_internal_transfer"
        @bulk_edit_transactions_bridge="bulk_edit_transactions_bridge"
        @bulk_edit_delete_confirm="bulk_edit_delete_confirm"
      />


      <!-- Update counter value dialog -->
      <q-dialog v-model="counter_value_dialog_show">
        <q-card
          class="q-pa-md"
          style="width: 540px; max-width: 90%"
        >
          <q-form @submit.prevent="counter_value_dialog_submit">
            <q-card-section class="row items-center">
              <div class="text-h6">
                {{ i18n.counter_values_dialog_title }}
              </div>
              <q-space />
              <q-btn
                v-close-popup
                icon="close"
                flat
                round
                dense
              />
            </q-card-section>

            <q-card-section class="row q-col-gutter-md q-py-none">
              <div class="col-12">
                {{ counter_value_dialog_transaction.fiat_value }}
                <q-input
                  v-model.number="counter_value_dialog_fiat_amount"
                  autofocus
                  outlined
                  dense
                  class="full-width q-mt-sm"
                  type="number"
                  placeholder="Enter new counter value"
                  :rules="[(val) => val !== null || i18n.required_field]"
                  :suffix="i18n.counter_values_dialog_suffix"
                />

                <transition
                  appear
                  enter-active-class="animated fadeIn"
                  leave-active-class="animated fadeOut"
                >
                  <q-banner
                    v-show="(counter_value_dialog_transaction.fiat_value < 0 && counter_value_dialog_fiat_amount > 0) ||
                      (counter_value_dialog_transaction.fiat_value > 0 && counter_value_dialog_fiat_amount < 0)
                    "
                    class="bg-pastel-green text-dark q-pa-md"
                    inline-actions
                  >
                    <template v-slot:avatar>
                      <q-icon name="las la-exclamation-triangle" />
                    </template>
                    {{ i18n.counter_values_dialog_warning_changing_value_sign }}
                  </q-banner>
                </transition>
              </div>
            </q-card-section>

            <q-card-actions
              align="right"
              class="q-mt-md"
            >
              <q-btn
                :label="$q.lang.label.cancel"
                color="primary"
                flat
                @click="counter_value_dialog_show = false"
              />
              <q-btn
                :label="i18n.counter_values_dialog_use_default_value"
                color="primary"
                outline
                unelevated
                @click="counter_value_dialog_update(null)"
              />
              <q-btn
                type="submit"
                :label="$q.lang.label.update"
                color="primary"
                unelevated
                :disable="counter_value_dialog_fiat_amount === null || counter_value_dialog_fiat_amount === ''"
              />
            </q-card-actions>
          </q-form>
        </q-card>
      </q-dialog>

      <!-- Edit transaction amount dialog -->
      <q-dialog
        v-model="edit_amount_dialog_show"
        @hide="resetEditAmountDialog"
      >
        <q-card
          class="q-pa-md"
          style="width: 540px; max-width: 90%"
        >
          <q-form
            ref="edit_amount_dialog_form_ref"
            @submit.prevent="submitEditAmountDialog"
          >
            <q-card-section class="row items-center">
              <div class="text-h6">
                {{ i18n.transaction_actions_edit_amount_title }}
              </div>
              <q-space />
              <q-btn
                icon="close"
                flat
                round
                dense
                @click="closeEditAmountDialog"
              />
            </q-card-section>

            <q-card-section class="row q-col-gutter-md q-py-none">
              <div class="col-12">
                <div v-if="edit_amount_dialog_reference_amount !== null && edit_amount_dialog_suffix">
                  {{ formatAmount(edit_amount_dialog_reference_amount, edit_amount_dialog_suffix) }}
                </div>
                <q-input
                  v-model="edit_amount_dialog_form.amount"
                  autofocus
                  outlined
                  dense
                  class="full-width q-mt-sm"
                  type="number"
                  step="any"
                  :rules="[(val) => val !== null && val !== '' || i18n.required_field]"
                  :suffix="edit_amount_dialog_suffix"
                />
              </div>
            </q-card-section>

            <q-card-actions
              align="right"
              class="q-mt-md"
            >
              <q-btn
                :label="$q.lang.label.cancel"
                color="primary"
                flat
                @click="closeEditAmountDialog"
              />
              <q-btn
                v-if="edit_amount_dialog_has_original"
                :label="i18n.transaction_actions_use_original_amount"
                color="primary"
                outline
                unelevated
                :loading="edit_amount_dialog_loading"
                :disable="edit_amount_dialog_loading"
                @click="editAmountDialogUseOriginalAmount"
              />
              <q-btn
                type="submit"
                :label="$q.lang.label.update"
                color="primary"
                unelevated
                :loading="edit_amount_dialog_loading"
                :disable="edit_amount_dialog_form.amount === null || edit_amount_dialog_form.amount === ''"
              />
            </q-card-actions>
          </q-form>
        </q-card>
      </q-dialog>

      <!-- Edit transaction time dialog -->
      <q-dialog
        v-model="edit_time_dialog_show"
        @hide="resetEditTimeDialog"
      >
        <q-card
          class="q-pa-md"
          style="width: 540px; max-width: 90%"
        >
          <q-form
            ref="edit_time_dialog_form_ref"
            @submit.prevent="submitEditTimeDialog"
          >
            <q-card-section class="row items-center">
              <div class="text-h6">
                {{ i18n.transaction_actions_edit_time_title }}
              </div>
              <q-space />
              <q-btn
                icon="close"
                flat
                round
                dense
                @click="closeEditTimeDialog"
              />
            </q-card-section>

            <q-card-section class="row q-col-gutter-md q-py-none">
              <div class="col-12">
                <div v-if="edit_time_dialog_transaction?.datetime">
                  {{ edit_time_dialog_transaction.datetime }}
                </div>
                <div class="text-caption text-muted q-mt-xs q-mb-md">
                  {{ i18n.transaction_actions_edit_time_message }}
                </div>
              </div>

              <div class="col-12">
                <span class="text-input-label">
                  {{ i18n.transactions_add_form_date }}
                </span>

                <q-input
                  v-model="edit_time_dialog_form.date"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  mask="date"
                  :rules="['date']"
                >
                  <template v-slot:append>
                    <q-icon
                      name="event"
                      class="cursor-pointer"
                    >
                      <q-popup-proxy
                        cover
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          v-model="edit_time_dialog_form.date"
                          minimal
                        >
                          <div class="row items-center justify-end">
                            <q-btn
                              v-close-popup
                              :label="$q.lang.label.close"
                              color="primary"
                              flat
                            />
                          </div>
                        </q-date>
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
              </div>

              <div class="col-12">
                <span class="text-input-label">
                  {{ i18n.transactions_add_form_time }}
                </span>

                <q-input
                  v-model="edit_time_dialog_form.time"
                  class="full-width q-mt-sm"
                  outlined
                  dense
                  mask="fulltime"
                  :rules="['fulltime']"
                >
                  <template v-slot:append>
                    <q-icon
                      name="schedule"
                      class="cursor-pointer"
                    >
                      <q-popup-proxy
                        cover
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-time
                          v-model="edit_time_dialog_form.time"
                          with-seconds
                          format24h
                        >
                          <div class="row items-center justify-end">
                            <q-btn
                              v-close-popup
                              :label="$q.lang.label.close"
                              color="primary"
                              flat
                            />
                          </div>
                        </q-time>
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
              </div>
            </q-card-section>

            <q-card-actions
              align="right"
              class="q-mt-md"
            >
              <q-btn
                :label="$q.lang.label.cancel"
                color="primary"
                flat
                @click="closeEditTimeDialog"
              />
              <q-btn
                v-if="edit_time_dialog_has_original"
                :label="i18n.transaction_actions_use_original_time"
                color="primary"
                outline
                unelevated
                :loading="edit_time_dialog_loading"
                :disable="edit_time_dialog_loading"
                @click="editTimeDialogUseOriginalTime"
              />
              <q-btn
                type="submit"
                :label="$q.lang.label.update"
                color="primary"
                unelevated
                :loading="edit_time_dialog_loading"
              />
            </q-card-actions>
          </q-form>
        </q-card>
      </q-dialog>

      <!-- AddressMapping - Dialog for renaming address mapping -->
      <q-dialog v-model="address_mappings_dialog_show">
        <q-card
          class="q-pa-md"
          style="width: 540px; max-width: 90%"
        >
          <q-card-section class="q-pb-sm">
            <div class="text-h6">
              {{
                false
                  ? i18n.address_mappings_create_title
                  : i18n.address_mappings_update_title
              }}
            </div>
            <p class="q-mt-md q-mb-none text-bold">
              {{ i18n.address_mappings_address }}
            </p>
            <p class="q-mb-sm">
              {{ address_mappings_dialog_address }}

              <!-- mini spinner when :loading="ensLookupLoading" -->
              <q-spinner
                v-if="ensLookupLoading"
                color="primary"
                size="16px"
                class="q-ml-xs"
              />
            </p>
          </q-card-section>

          <q-card-section class="row q-col-gutter-md q-pb-none">
            <!-- Input for Address Mapping Name -->
            <div class="col-12">
              <span class="text-bold text-muted">
                {{ i18n.address_mappings_name }}
              </span>
              <q-input
                v-model="address_mappings_dialog_name"
                autofocus
                outlined
                dense
                class="full-width q-mt-sm"
                :placeholder="i18n.address_mappings_name_placeholder"
                :rules="[
                  (val) => val?.trim() !== '' || i18n.required_field,
                ]"
              />
            </div>
          </q-card-section>

          <!-- Suggested ENS name -->
          <transition
            appear
            enter-active-class="animated fadeInLeft"
            :duration="2000"
          >
            <q-card-section v-show="ensSuggestedName != null">
              <q-item class="q-pa-none">
                <q-item-section avatar>
                  <q-icon
                    name="las la-info-circle"
                    color="primary"
                    size="42px"
                  />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="text-bold text-muted">
                    {{
                      $t("accounting.ensNameAvailable")
                    }}
                  </q-item-label>
                  <q-item-label class="text-bold text-accent">
                    {{
                      ensSuggestedName
                    }}
                  </q-item-label>
                </q-item-section>

                <q-item-section side>
                  <q-btn
                    no-caps
                    :label="$t('general.use')"
                    color="accent"
                    unelevated
                    @click="address_mappings_dialog_name = ensSuggestedName"
                  />
                </q-item-section>
              </q-item>
            </q-card-section>
          </transition>

          <q-card-actions
            align="right"
            class="q-mt-md"
          >
            <q-btn
              :label="$q.lang.label.cancel"
              color="primary"
              flat
              @click="address_mappings_dialog_show = false"
            />
            <q-btn
              :label="i18n.address_mappings_rename_action"
              color="primary"
              unelevated
              :disable="!address_mappings_dialog_name ||
                address_mappings_dialog_name.trim() === ''
              "
              @click="address_mappings_dialog_submit"
            />
          </q-card-actions>

          <!-- Loading overlay showing -->
          <q-inner-loading :showing="address_mappings_dialog_loading">
            <q-spinner-tail
              color="primary"
              size="50px"
            />
          </q-inner-loading>
        </q-card>
      </q-dialog>

      <!-- Change Rate Warning - Dialog to show market price availables fixes -->
      <q-dialog
        v-model="transactions_missing_change_rate_dialog_show"
        class="q-pa-md"
      >
        <q-card
          style="max-width: 740px; width: 90%"
          class="q-pa-md"
        >
          <q-card-section class="row items-center q-pb-none">
            <div class="text-h5 text-bold">
              {{ i18n.transactions_missing_change_rate_dialog_title }}
            </div>
            <q-space />
            <q-btn
              v-close-popup
              icon="close"
              flat
              round
              dense
            />
          </q-card-section>

          <q-card-section class="q-pt-md q-px-md q-pb-none">
            <p v-html="transactions_missing_change_rate_dialog_transaction.change_rate_missing.description" />

            <br>
            <p class="q-mb-none">
              {{ i18n.transactions_missing_change_rate_dialog_options }}
            </p>
          </q-card-section>

          <q-card-section class="row q-col-gutter-md">
            <!-- Option 1: Manual price -->
            <div class="col-12">
              <q-card
                class="q-pa-md fit cursor-pointer q-hoverable"
                @click="transactions_missing_change_rate_dialog_manual_update"
              >
                <span class="q-focus-helper" />
                <q-item class="q-pa-none">
                  <q-item-section avatar>
                    <q-avatar
                      size="lg"
                      color="pastel-purple"
                    >
                      <q-icon
                        name="las la-pen"
                        size="24px"
                        color="black"
                      />
                    </q-avatar>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-bold">
                      {{ i18n.transactions_missing_change_rate_dialog_option_1_title }}
                    </q-item-label>
                    <q-item-label
                      caption
                      class="text-caption text-muted"
                    >
                      {{ i18n.transactions_missing_change_rate_dialog_option_1_desc }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-card>
            </div>

            <!-- Option 2: Use provider -->
            <div class="col-12">
              <Link :href="transactions_missing_change_rate_dialog_transaction.change_rate_missing.exchange_asset_change_rate_link">
                <q-card
                  v-close-popup
                  class="q-pa-md fit cursor-pointer q-hoverable"
                >
                  <span class="q-focus-helper" />
                  <q-item class="q-pa-none">
                    <q-item-section avatar>
                      <q-avatar
                        size="lg"
                        color="pastel-orange"
                      >
                        <q-icon
                          name="las la-chart-line"
                          size="24px"
                          color="black"
                        />
                      </q-avatar>
                    </q-item-section>
                    <q-item-section>
                      <q-item-label class="text-bold">
                        {{ i18n.transactions_missing_change_rate_dialog_option_2_title }}
                      </q-item-label>
                      <q-item-label
                        caption
                        class="text-caption text-muted"
                      >
                        {{ i18n.transactions_missing_change_rate_dialog_option_2_desc }}
                      </q-item-label>
                    </q-item-section>
                  </q-item>
                </q-card>
              </Link>
            </div>
          </q-card-section>

          <q-card-section class="row q-col-gutter-md text-center">
            <!-- documentation-chip -->
            <div class="col-12">
              <span class="text-caption">{{ i18n.transactions_missing_change_rate_dialog_help_label }}</span>
              <q-chip
                clickable
                color="pastel-green"
                size="12px"
                class="q-ml-sm"
                text-color="black"
                icon="menu_book"
                :label="i18n.transactions_missing_change_rate_dialog_help_title"
                @click="$intercom.article_open('11829617')"
              />
            </div>
          </q-card-section>
        </q-card>
      </q-dialog>

      <!-- Transaction Kind - Dialog to select the transaction kind -->
      <q-dialog
        v-model="transactions_kind_dialog_show"
        full-width
        maximized
      >
        <q-card class="p-8">
          <div class="centered-container-large q-pb-none">
            <!-- Header -->
            <q-card-section class="row items-center q-pb-none">
              <div class="text-h5">
                {{ i18n.transactions_kind_dialog_title }}
              </div>
              <q-space />
              <q-btn
                icon="close"
                flat
                dense
                round
                no-caps
                @click="transactions_kind_dialog_show = false"
              />
            </q-card-section>

            <q-card-section
              v-if="transactions_kind_dialog_transaction.summary"
              class="row items-center q-pb-none q-pt-none"
            >
              <p
                class="q-mb-none"
                v-html="transactions_kind_dialog_transaction.summary"
              />
            </q-card-section>

            <q-card-section class="row items-center q-pb-none q-pt-none q-mb-xl">
              <!-- Display accounting plan name -->
              <template v-if="shared.workspace.is_business && accounting_plan_name">
                <p class="">
                  {{ i18n.transactions_kind_dialog_accounting_plan_label }}: <b>{{ accounting_plan_name }}</b>
                </p>
              </template>
            </q-card-section>

            <!-- Search bar -->
            <q-card-section class="row items-center q-pt-none">
              <q-input
                v-model="transactions_kind_dialog_search"
                rounded
                outlined
                debounce="100"
                dense
                clearable
                clear-value=""
                :placeholder="i18n.transactions_kind_dialog_search_placeholder"
                prepend-icon="search"
                class=""
                style="min-width: 360px;"
                autofocus
              >
                <template v-slot:prepend>
                  <q-icon name="search" />
                </template>
              </q-input>
            </q-card-section>

            <!-- Categories and Kinds -->
            <q-card-section
              v-if="transactions_kind_dialog_categories.length"
              class="row q-pt-md q-col-gutter-lg justify-start"
            >
              <div
                v-for="category in transactions_kind_dialog_categories"
                :key="category.name"
                class="col-auto"
              >
                <q-item class="q-mb-sm">
                  <q-item-section avatar>
                    <q-avatar
                      :color="category.icon.color"
                      size="32px"
                      class="q-mr-sm"
                    >
                      <q-icon
                        :name="category.icon.class"
                        color="black"
                        size="18px"
                        class=""
                      />
                    </q-avatar>
                  </q-item-section>

                  <q-item-section>
                    <q-item-label class="text-bold">
                      {{ category.label }}
                    </q-item-label>
                  </q-item-section>
                </q-item>

                <q-list dense>
                  <q-item
                    v-for="kind in category.transaction_kinds"
                    :key="kind.id"
                    clickable
                    active-class="bg-active-item"
                    :active="kind.id == transactions_kind_dialog_transaction.kind"
                    :disable="kind.name == 'trade_in' || kind.name == 'trade_out'"
                    @click="transactions_kind_dialog_select(kind)"
                  >
                    <q-item-section>
                      <q-item-label class="">
                        {{ kind.label }}

                        <!-- Add info icon if trade in ou trade out -->
                        <q-icon
                          v-if="kind.name == 'trade_in' || kind.name == 'trade_out'"
                          name="info"
                          color="primary"
                          class="q-ml-xs"
                        />
                      </q-item-label>
                      <q-item-label
                        v-if="kind.description && !shared.workspace.is_business"
                        caption
                        class="text-caption text-muted"
                        style="max-width: 320px;"
                      >
                        {{ kind.description }}
                      </q-item-label>
                      <q-tooltip
                        v-if="kind.description && shared.workspace.is_business"
                        anchor="center right"
                        self="center left"
                        class="transaction-tooltip q-card shadow text-caption text-default q-pa-md q-menu"
                        style="width: 300px"
                        :delay="500"
                      >
                        <p class="q-mb-none">
                          {{ kind.description }}
                        </p>

                        <template v-if="kind.name == 'trade_in' || kind.name == 'trade_out'">
                          <p class="q-mt-md text-default text-bold">
                            {{ i18n.transactions_kind_dialog_trades_warn }}
                          </p>
                        </template>
                      </q-tooltip>
                    </q-item-section>

                    <!-- If active, add a delete option button -->
                    <q-item-section
                      v-if="kind.id === transactions_kind_dialog_transaction.kind"
                      side
                    >
                      <q-btn
                        unelevated
                        icon="close"
                        dense
                        round
                        size="sm"
                        @click.stop="transactions_kind_dialog_select('uncategorized')"
                      >
                        <template v-slot:default>
                          <q-tooltip
                            anchor="top middle"
                            self="bottom middle"
                          >
                            <q-item-label>{{ i18n.transactions_kind_dialog_uncategorize }}</q-item-label>
                          </q-tooltip>
                        </template>
                      </q-btn>
                    </q-item-section>
                  </q-item>
                </q-list>
              </div>
            </q-card-section>

            <q-card-section
              v-else-if="transactions_kind_dialog_no_results"
              class="text-center q-pa-xl text-grey-7"
            >
              <q-icon
                name="search_off"
                size="md"
                color="grey-7"
              />
              <p class="text-caption q-mt-sm q-mb-none">
                {{ i18n.transactions_kind_dialog_search_no_results }}
              </p>
            </q-card-section>
          </div>
        </q-card>
      </q-dialog>
    </q-page>

    <TransactionsDetailsSidebar
      v-if="transaction_details != void 0"
      ref="transactionsDetailsSidebar"
      v-model:show="transaction_details_section_show"
      :transaction_details="transaction_details"
      :i18n="i18n"
      :shared="shared"
      :transaction_context_menu_items="transactionDetailsContextMenuItems"
      :loading="transactions_details_sidebar_loading"
      :transactions_update_loading="transactions_update_loading"
      :transactions_delete_loading="transactions_delete_loading"
      :address_mappings="address_mappings"
      :supporting_documents_create_action_path="supporting_documents_create_action_path"
      :supporting_documents_destroy_action_path="supporting_documents_destroy_action_path"
      :onboarding-steps="onboardingSteps"
      :wrapper-options="wrapperOptions"
      @open-counter-value="counter_value_dialog_open"
      @open-kind-dialog="transactions_kind_dialog_open"
      @duplicate="duplicateMovement"
      @edit-amount="editAmountDialog"
      @restore-amount="restoreOriginalAmount"
      @edit-time="openEditTimeDialog"
      @restore-time="restoreOriginalTime"
      @filter-hash="filterTransactionsByHashValue"
      @update-transaction="transactions_update"
      @delete-transaction="transactions_delete_confirm"
      @open-address-mapping="address_mappings_dialog_open"
      @onboarding-finish="handleOnboardingFinish"
      @close="transactions_details_sidebar_close"
    />

    <!-- Create Categorization Rule Dialog -->
    <q-dialog
      v-model="create_categorization_rule_dialog_show"
      persistent
    >
      <q-card class="transactions-settings-dialog__form q-pa-lg">
        <q-card-section class="row items-center q-pb-none">
          <div class="col text-h6">
            {{ i18n.create_categorization_rule_dialog_title }}
          </div>
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>

        <q-card-section class="q-pt-none">
          <div class="text-secondary-caption">
            {{ i18n.create_categorization_rule_dialog_description }}
          </div>
        </q-card-section>

        <q-card-section class="q-pt-md">
          <q-form @submit.prevent="create_categorization_rule_submit">
            <div class="q-mb-lg">
              <span class="text-input-label">{{ i18n.create_categorization_rule_name_label }}</span>
              <q-input
                v-model="create_categorization_rule_form.name"
                outlined
                dense
                hide-bottom-space
                class="full-width q-mt-sm"
                :rules="[
                  (val) => !!val || i18n.required_field,
                  (val) => !val || val.length <= 48 || '48 caractères maximum'
                ]"
              />
            </div>

            <div class="q-mb-lg">
              <span class="text-input-label">{{ i18n.create_categorization_rule_category_label }}</span>
              <q-select
                v-model="create_categorization_rule_form.transaction_kind_id"
                outlined
                dense
                options-dense
                :options="[]"
                hide-bottom-space
                option-label="label"
                option-value="id"
                class="full-width q-mt-sm"
                popup-content-class="transactions_kind_selector_popup q-pa-sm"
                emit-value
                map-options
                :rules="[(val) => !!val || 'Champ requis']"
              >
                <template v-slot:selected>
                  <div v-if="create_categorization_rule_form.transaction_kind_id">
                    {{ create_categorization_rule_selected_kind_label }}
                  </div>
                </template>

                <template v-slot:no-option>
                  <div class="transactions_kind_selector_panel">
                    <div class="transactions_kind_selector_content">
                      <div class="transactions_kind_selector_header q-pt-none q-px-none">
                        <q-input
                          v-model="create_categorization_rule_kind_search"
                          outlined
                          dense
                          :placeholder="transactions_filters.kinds_placeholder"
                          autofocus
                        >
                          <template v-slot:prepend>
                            <q-icon name="search" />
                          </template>
                          <template v-slot:append>
                            <q-icon
                              v-if="create_categorization_rule_kind_search"
                              name="close"
                              class="cursor-pointer"
                              @click="create_categorization_rule_kind_search = ''"
                            />
                          </template>
                        </q-input>
                      </div>
                      <div class="transactions_kind_selector_scroll q-pa-sm">
                        <q-list dense>
                          <template
                            v-for="(category) in create_categorization_rule_kinds_filtered"
                            :key="category.name"
                          >
                            <template v-if="category.kinds.length > 0">
                              <q-item-label
                                header
                                class="text-capitalize text-bold"
                              >
                                <q-avatar
                                  :color="category.icon.color"
                                  size="28px"
                                  class="q-mr-sm"
                                >
                                  <q-icon
                                    :name="category.icon.class"
                                    color="black"
                                    size="18px"
                                  />
                                </q-avatar>
                                {{ category.label }}
                              </q-item-label>

                              <q-item
                                v-for="kind in category.kinds"
                                :key="kind.id"
                                v-close-popup
                                clickable
                                :active="create_categorization_rule_form.transaction_kind_id == kind.id"
                                :class="{ 'text-accent text-bold': create_categorization_rule_form.transaction_kind_id == kind.id }"
                                @click="
                                  create_categorization_rule_kind_search = '';
                                  create_categorization_rule_form.transaction_kind_id = kind.id;
                                "
                              >
                                <q-item-section>
                                  <q-item-label>
                                    {{ kind.label }}
                                  </q-item-label>
                                </q-item-section>
                              </q-item>

                              <q-separator />
                            </template>
                          </template>
                        </q-list>
                      </div>
                    </div>
                  </div>
                </template>
              </q-select>
            </div>

            <div class="q-mb-lg">
              <span class="text-input-label">{{ i18n.create_categorization_rule_active_filters }}</span>
              <div class="q-mt-sm">
                <div
                  v-if="create_categorization_rule_filters_preview.length"
                  class="rule-filters-grid"
                >
                  <q-chip
                    v-for="filter in create_categorization_rule_filters_preview"
                    :key="filter.key"
                    class="rule-filter-chip p-sm"
                  >
                    <span class="text-bold">{{ filter.label }}:</span>
                    <span class="rule-filter-chip__value q-ml-xs">{{ filter.value }}</span>
                  </q-chip>
                </div>
                <div
                  v-else
                  class="text-muted text-body2"
                >
                  {{ i18n.no_active_filters || 'Aucun filtre actif' }}
                </div>
              </div>
            </div>

            <div class="text-right q-mt-lg">
              <q-btn
                flat
                :label="$q.lang.label.cancel"
                color="primary"
                class="q-mr-md"
                @click="create_categorization_rule_dialog_show = false"
              />
              <q-btn
                type="submit"
                label="Créer"
                color="primary"
                unelevated
                :loading="create_categorization_rule_loading"
              />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>
<script>
import Layout from '@/layouts/workspace.vue';
import { VOnboardingWrapper, VOnboardingStep } from 'v-onboarding';
import TransactionsSettingsDialog from '@/pages/w/transactions/transactions_settings_dialog.vue';
import TransactionsFilters from '@/pages/w/transactions/transactions_filters.vue';
import TransactionsTable from '@/pages/w/transactions/transactions_table.vue';
import TransactionsBulkEdit from '@/pages/w/transactions/transactions_bulk_edit.vue';
import TransactionsDetailsSidebar from '@/pages/w/transactions/transactions_details_sidebar.vue';
import { toRaw } from 'vue';
import { usePoll } from '@inertiajs/vue3';
import { Link } from '@inertiajs/vue3';
import { copyToClipboard } from 'quasar';

export default {
  components: {
    Link,
    TransactionsSettingsDialog,
    TransactionsFilters,
    TransactionsTable,
    TransactionsBulkEdit,
    TransactionsDetailsSidebar,
    VOnboardingStep,
    VOnboardingWrapper,
  },
  layout: Layout,
  props: {
    shared: {
      type: Object,
      required: true,
    },
    transactions_counts: {
      type: Object,
      required: true,
    },
    i18n: {
      type: Object,
      required: true,
    },
    workspace_settings: {
      type: Object,
      required: true,
    },
    search_params: {
      type: Object,
      required: true,
    },
    wac: {
      type: Object,
      required: true,
    },
    imports_page_action_path: {
      type: String,
      required: true,
    },
    transaction_create_action_path: {
      type: String,
      required: true,
    },
    exchange_asset_create_action_path: {
      type: String,
      required: true,
    },
    supporting_documents_download_all_action_path: {
      type: String,
      required: true,
    },
    supporting_documents_create_action_path: {
      type: String,
      required: true,
    },
    supporting_documents_destroy_action_path: {
      type: String,
      required: true,
    },
    transactions_update_by_id_action_path: {
      type: String,
      required: true,
    },
    transactions_update_by_search_action_path: {
      type: String,
      required: true,
    },
    transactions_delete_by_id_action_path: {
      type: String,
      required: true,
    },
    transactions_delete_by_search_action_path: {
      type: String,
      required: true,
    },
    transactions_delete_movement_action_path: {
      type: String,
      required: true,
    },
    transactions_trade_create_action_path: {
      type: String,
      required: true,
    },
    transactions_internal_transfer_create_action_path: {
      type: String,
      required: true,
    },
    transactions_bridge_create_action_path: {
      type: String,
      required: true,
    },
    transactions_export_path: {
      type: String,
      required: true,
    },
    address_mappings_update_action_path: {
      type: String,
      required: true,
    },
    accounting_plan_name: {
      type: String,
      required: true,
    },
    address_mappings: {
      type: Object,
      required: true,
    },
    transaction_kind_categories: {
      type: Object,
      required: true,
    },
    transactions_add_exchange_assets_selected: {
      type: Object,
      required: false,
    },
    transactions_add_exchange_assets: {
      type: Object,
      required: true,
    },
    transactions: {
      type: Object,
      required: true,
    },
    transactions_filters: {
      type: Object,
      required: true,
    },
    transactions_settings: {
      type: Object,
      required: true,
    },
    transactions_settings_state: {
      type: Object,
      required: true,
    },
    categorization_rules_link: {
      type: String,
      required: false,
    },
    transaction_details: {
      type: Object,
      required: false,
    },
  },
  data() {
    const poll = usePoll(
      1000,
      {
        only: ["wac"],
        data: { ajax_bar_skip: '' },
      },
      { autoStart: this.wac.polling }
    );

    return {
      wac_polling_start: poll.start,
      wac_polling_stop: poll.stop,
      onboardingSteps: [],
      wrapperOptions: {},
      transactions_delete_loading: false,
      transactions_update_loading: false,
      transactions_kind_dialog_show: false,
      transactions_kind_dialog_transaction: {},
      transactions_kind_dialog_search: '',
      transactions_add_dialog_show: false,
      transactions_add_dialog_loading: false,
      transactions_add_dialog_form: {},
      transactions_add_dialog_form_transaction_in_asset_error: false,
      transactions_add_dialog_form_transaction_out_asset_error: false,
      transactions_add_dialog_form_sender_show: false,
      transactions_add_dialog_form_recipient_show: false,
      transactions_add_dialog_form_types: [
        {
          value: "credit",
          label: this.i18n.transactions_add_form_incoming,
          icon: "las la-arrow-down",
          color: "positive",
        },
        {
          value: "debit",
          label: this.i18n.transactions_add_form_outgoing,
          icon: "las la-arrow-up",
          color: "negative",
        },

        {
          value: "swap",
          label: this.i18n.transactions_add_form_swap,
          icon: "las la-exchange-alt",
          color: "info",
        },
      ],
      create_categorization_rule_dialog_show: false,
      create_categorization_rule_loading: false,
      create_categorization_rule_kind_search: '',
      create_categorization_rule_form: {
        name: '',
        transaction_kind_id: null,
      },
      categorization_rules: [],
      transactions_assets_add_dialog_show: false,
      transactions_assets_add_dialog_loading: false,
      transactions_assets_add_direction: 'in', // 'in', 'out'
      transactions_assets_add_form: {},
      transactions_missing_change_rate_dialog_show: false,
      transactions_missing_change_rate_dialog_transaction: {},
      state_transactions_meta: structuredClone(toRaw(this.transactions.meta)),
      state_search_params: structuredClone(toRaw(this.search_params)),
      state_transactions_filters: structuredClone(toRaw(this.transactions_filters)),
      transactions_loading: false,
      transactions_selection: {}, // Selected Transactions across pages.
      transactions_all_pages_selected: false, // Select everything.
      autoOpenCounterValue: '',
      showMergeButton: false,
      isLoadingTransactions: false,
      pagination: {},
      bulk_edit_search: '',
      bulk_edit_action_loading: false,
      showTransactionDetailsID: '',
      recentTransactionKinds: [],
      transactions_details_sidebar_loading: false,
      transaction_details_section_show: true,
      pending_accounting_details_movement_id: null,
      address_mappings_dialog_show: false,
      address_mappings_dialog_transaction: {},
      address_mappings_dialog_address: '',
      address_mappings_dialog_name: '',
      address_mappings_dialog_auxiliary_account_number: '',
      address_mappings_dialog_loading: false,
      counter_value_dialog_show: false,
      counter_value_dialog_transaction: {},
      edit_amount_dialog_show: false,
      edit_amount_dialog_loading: false,
      edit_amount_dialog_form: {
        amount: '',
      },
      edit_amount_dialog_transaction: null,
      edit_amount_dialog_suffix: '',
      edit_amount_dialog_reference_amount: null,
      edit_amount_dialog_has_original: false,
      edit_amount_dialog_original_amount: null,
      edit_time_dialog_show: false,
      edit_time_dialog_loading: false,
      edit_time_dialog_form: {
        date: '',
        time: '',
      },
      edit_time_dialog_has_original: false,
      edit_time_dialog_transaction: null,
      counter_value_dialog_fiat_amount: 0,
    };
  },
  computed: {
    filters_filled_count() {
      const params = this.state_search_params;
      const filtered = Object.fromEntries(
        Object.entries(params).filter(([key]) => ![
          'date_to',
          'order_by',
          'order_direction',
          'page',
          'per_page',
          'w_id',
        ].includes(key))
      );
      return Object.keys(filtered).length;
    },
    filters_clear_show() {
      return this.filters_filled_count > 0;
    },
    filters_exchange_accounts_selected() {
      if (!this.state_search_params.exchange_account_id) {
        return [];
      }
      return this.transactions_filters.exchange_accounts.filter((ea) => {
        return this.state_search_params.exchange_account_id.includes(ea.id);
      });
    },
    filters_assets_selected() {
      if (!this.state_search_params.asset_id) {
        return [];
      }
      return this.transactions_filters.assets.filter((a) => {
        return this.state_search_params.asset_id.includes(a.id);
      });
    },
    filters_kinds_selected() {
      if (!this.state_search_params.kind) {
        return [];
      }
      return this.transactions_filters.kinds.filter((k) => {
        return this.state_search_params.kind.includes(k.id);
      });
    },
    filters_import_sources_selected() {
      if (!this.state_search_params.import_source_id) {
        return [];
      }
      let import_sources = [];
      this.transactions_filters.import_sources.forEach((ea) => {
        ea.import_sources.forEach((is) => {
          if (is.selected) {
            import_sources.push(is);
          }
        });
      });
      return import_sources;
    },
    filters_exchange_assets_selected() {
      const ids = new Set((this.state_search_params.exchange_asset_id || []).map((id) => String(id)));
      if (ids.size === 0) {
        return [];
      }
      return (this.transactions_filters.exchange_assets_selected || []).filter((asset) => ids.has(String(asset.id)));
    },
    create_categorization_rule_kinds_filtered() {
      let categories = [];
      let categories_index = -1;

      this.transactions_filters.kinds.forEach((tk) => {
        // Skip trade_in and trade_out
        if (tk.name === 'trade_in' || tk.name === 'trade_out') {
          return;
        }

        let category = categories[categories_index];
        if (category?.name != tk.category_name) {
          categories_index += 1;
          category = {
            name: tk.category_name,
            icon: tk.icon,
            label: tk.category_label,
            kinds: [],
          };
          categories[categories_index] = category;
        }

        if (!this.create_categorization_rule_kind_search || tk.label.toLowerCase().includes(this.create_categorization_rule_kind_search.toLowerCase())) {
          category.kinds.push(tk);
        }
      });

      return categories;
    },
    create_categorization_rule_selected_kind_label() {
      const kind = this.transactions_filters.kinds.find(k => k.id === this.create_categorization_rule_form.transaction_kind_id);
      return kind ? kind.label : '';
    },
    create_categorization_rule_filters_preview() {
      const filters = this.build_categorization_rule_filters();
      const chips = [];

      if (filters.exchange_account_id?.length) {
        chips.push({
          key: 'exchange_account_id',
          label: this.transactions_filters.exchange_accounts_label,
          value: this.filters_exchange_accounts_selected.map((ea) => ea.name).join(', ') || filters.exchange_account_id.join(', '),
        });
      }

      if (filters.asset_id?.length) {
        chips.push({
          key: 'asset_id',
          label: this.transactions_filters.assets_label,
          value: this.filters_assets_selected.map((asset) => asset.symbol).join(', ') || filters.asset_id.join(', '),
        });
      }

      if (filters.kind?.length) {
        chips.push({
          key: 'kind',
          label: this.transactions_filters.kinds_label,
          value: this.filters_kinds_selected.map((kind) => kind.label).join(', ') || filters.kind.join(', '),
        });
      }

      if (filters.uncategorized) {
        chips.push({
          key: 'uncategorized',
          label: this.transactions_filters.kinds_status,
          value: this.transactions_filters.uncategorized_label,
        });
      }

      if (filters.date_from || filters.date_to) {
        chips.push({
          key: 'date_range',
          label: this.transactions_filters.dates_label,
          value: `${filters.date_from || '…'} → ${filters.date_to || '…'}`,
        });
      }

      if (filters.change_rate) {
        const changeRateLabel = this.transactions_filters.change_rates.find((cr) => cr.value === filters.change_rate)?.name || filters.change_rate;
        chips.push({
          key: 'change_rate',
          label: this.transactions_filters.change_rate_label,
          value: changeRateLabel,
        });
      }

      if (filters.type) {
        const typeLabel = this.transactions_filters.types.find((type) => type.value === filters.type)?.name || filters.type;
        chips.push({
          key: 'type',
          label: this.transactions_filters.direction_label,
          value: typeLabel,
        });
      }

      if (filters.manual !== undefined && filters.manual !== null && filters.manual !== '') {
        const manualLabel = this.transactions_filters.manual.find((manual) => manual.value === filters.manual)?.name || filters.manual;
        chips.push({
          key: 'manual',
          label: this.transactions_filters.manual_label,
          value: manualLabel,
        });
      }

      if (filters.amount_min) {
        chips.push({
          key: 'amount_min',
          label: this.transactions_filters.amount_min_label,
          value: filters.amount_min,
        });
      }

      if (filters.amount_max) {
        chips.push({
          key: 'amount_max',
          label: this.transactions_filters.amount_max_label,
          value: filters.amount_max,
        });
      }

      if (filters.from_address) {
        const fromAddress = Array.isArray(filters.from_address) ? filters.from_address.join(', ') : filters.from_address;
        chips.push({
          key: 'from_address',
          label: this.transactions_filters.sender_label,
          value: fromAddress,
        });
      }

      if (filters.to_address) {
        const toAddress = Array.isArray(filters.to_address) ? filters.to_address.join(', ') : filters.to_address;
        chips.push({
          key: 'to_address',
          label: this.transactions_filters.recipient_label,
          value: toAddress,
        });
      }

      if (filters.note) {
        chips.push({
          key: 'note',
          label: this.transactions_filters.note_label,
          value: filters.note,
        });
      }

      if (filters.exchange_asset_id?.length) {
        const exchangeAssets = this.filters_exchange_assets_selected;
        const exchangeAssetsLabel = exchangeAssets.length
          ? exchangeAssets.map((asset) => asset.exchange_symbol || asset.symbol || asset.name || asset.id).join(', ')
          : `${filters.exchange_asset_id.length} ${filters.exchange_asset_id.length > 1 ? 'sélectionnés' : 'sélectionné'}`;

        chips.push({
          key: 'exchange_asset_id',
          label: this.transactions_filters.exchange_assets_label,
          value: exchangeAssetsLabel,
        });
      }

      return chips;
    },
    create_categorization_rule_has_filters() {
      return this.create_categorization_rule_filters_preview.length > 0;
    },
    transactions_kind_dialog_categories() {
      const search = this.transactions_kind_dialog_search?.trim().toLowerCase();

      return this.transaction_kind_categories.reduce((categories, category) => {
        const transaction_kinds = category.transaction_kinds.filter((kind) => {
          if (!search) {
            return true;
          }

          return kind.label.toLowerCase().includes(search);
        });

        if (search && transaction_kinds.length === 0) {
          return categories;
        }

        categories.push({
          ...category,
          transaction_kinds,
        });

        return categories;
      }, []);
    },
    transactions_kind_dialog_no_results() {
      const search = this.transactions_kind_dialog_search?.trim();
      return !!search && this.transactions_kind_dialog_categories.length === 0;
    },
    bulk_edit_categories() {
      const search = this.bulk_edit_search?.trim().toLowerCase();

      if (search == '') {
        return this.transaction_kind_categories;
      }

      const categs = this.transaction_kind_categories.map(category => {
        const kinds = category.transaction_kinds.filter(kind => {
          return kind.label.toLowerCase().includes(search);
        });

        if (kinds.length == 0) {
          return null;
        }

        return {
          ...category,
          transaction_kinds: kinds,
        };
      }).filter(categ => categ != null);

      return categs;
    },
    transactions_selected() {
      return this.transactions.data.filter((t) => {
        return this.transactions_selection[t.id];
      });
    },
    transactions_selected_count() {
      return this.transactions_selected.length;
    },
    transactions_page_selected() {
      if (this.transactions_selected_count == this.transactions.data.length) {
        return true;
      } else if (this.transactions_selected_count > 0) {
        return null;
      } else {
        return false;
      }
    },
    // Determine single action to show based on selected transactions
    bulk_edit_single_action() {
      // Need at least 2 transactions selected
      if (this.transactions_selected_count < 2) {
        return null;
      }

      // 3+ transactions: only the internal transfer family is supported,
      // and only when every movement shares the same asset.
      if (this.transactions_selected_count > 2) {
        return this.bulk_edit_internal_transfer_action_multi();
      }

      const [t1, t2] = this.transactions_selected;
      const amount_1 = parseFloat(t1.amount);
      const amount_2 = parseFloat(t2.amount);

      // Must have opposite signs
      const hasOppositeSigns = (amount_1 > 0 && amount_2 < 0) || (amount_1 < 0 && amount_2 > 0);
      if (!hasOppositeSigns) {
        return null;
      }

      // Vérifier si même asset : soit même exchange_asset_id, soit même asset_id
      const same_asset = (
        (t1.exchange_asset_id && t2.exchange_asset_id && t1.exchange_asset_id === t2.exchange_asset_id) ||
        (t1.asset_id != null && t2.asset_id != null && t1.asset_id === t2.asset_id)
      );
      const same_account = t1.exchange_account_id === t2.exchange_account_id;

      // Cas 1: Même crypto dans les 2 sens
      if (same_asset) {
        const abs1 = Math.abs(amount_1);
        const abs2 = Math.abs(amount_2);

        if (abs1 === abs2) {
          return {
            type: 'internal_transfer',
            label: this.i18n.bulk_edit_internal_transfer_action,
            action: 'internal_transfer',
            enabled: true,
            disabledReason: null,
          };
        } else if ((amount_1 < 0 && abs1 > abs2) || (amount_2 < 0 && abs2 > abs1)) {
          // montant négatif > montant positif : commission
          return {
            type: 'internal_transfer_with_commission',
            label: this.i18n.bulk_edit_internal_transfer_with_commission_action,
            action: 'internal_transfer',
            enabled: true,
            disabledReason: null,
          };
        } else {
          // montant positif > montant négatif : airdrop
          return {
            type: 'internal_transfer_with_airdrop',
            label: this.i18n.bulk_edit_internal_transfer_with_airdrop_action,
            action: 'internal_transfer',
            enabled: true,
            disabledReason: null,
          };
        }
      }

      // Cas 2: Crypto différente sur le même compte
      if (same_account) {
        return {
          type: 'trade',
          label: this.i18n.bulk_edit_merge_action,
          action: 'merge',
          enabled: true,
          disabledReason: null,
        };
      }

      // Cas 3: Crypto différente sur des comptes différents
      return {
        type: 'bridge',
        label: this.i18n.bulk_edit_bridge_action,
        action: 'bridge',
        enabled: true,
        disabledReason: null,
      };
    },
    bulk_edit_action_description() {
      if (!this.bulk_edit_single_action) return null;

      // Mapper le type d'action vers le nom du transaction_kind
      const kindNameMap = {
        'internal_transfer': 'internal_transfer',
        'internal_transfer_with_commission': 'internal_transfer',
        'internal_transfer_with_airdrop': 'internal_transfer',
        'trade': 'trade_in',
        'bridge': 'bridge'
      };

      const kindName = kindNameMap[this.bulk_edit_single_action.type];
      if (!kindName) return null;

      // Chercher dans bulk_edit_categories
      for (const category of this.bulk_edit_categories) {
        const kind = category.transaction_kinds.find(tk => tk.name === kindName);
        if (kind && kind.description) {
          return kind.description;
        }
      }

      return null;
    },
    // show: false,
    // fiat_amount_total: 0.0,
    // fiat_amount_total_formatted: '0.0 EUR',
    // items: [
    //   {
    //     asset_id: '',
    //     asset_symbol: '',
    //     asset_icon: '',
    //     total: 0.0,
    //     total_formatted: '0.0 BTC',
    //   },
    // ],
    isTransactionDetailsSidebarOpen() {
      return !!(this.transaction_details && this.transaction_details_section_show);
    },
    transactionDetailsContextMenuItems() {
      if (!this.transaction_details) {
        return [];
      }
      if (typeof this.buildTransactionContextMenuItems !== 'function') {
        return [];
      }
      return this.buildTransactionContextMenuItems(this.transaction_details) || [];
    },
    transactions_selected_sums() {
      let show = false;
      let items = [];

      const formatter_asset = (value, symbol) => {
        let str = new Intl.NumberFormat("fr-FR", { // TODO: localize format
          minimumFractionDigits: 0,
          maximumFractionDigits: 4,
        }).format(value);
        str = `${str} ${symbol}`;
        if (value > 0) {
          str = `+${str}`;
        }
        return str;
      };
      const formatter_fiat = (value) => {
        let str = new Intl.NumberFormat("fr-FR", { // TODO: localize format
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
          style: "currency",
          currency: this.i18n.fiat,
        }).format(value);
        if (value > 0) {
          str = `+${str}`;
        }
        return str;
      };


      if (!this.transactions_all_pages_selected && this.transactions_selected.length > 0) {
        // Group transactions by asset_id and trade_in_asset_id
        const transactions_grouped = this.transactions_selected.reduce(
          (acc, transaction) => {
            if (transaction.asset_id) {
              (acc[transaction.asset_id] = acc[transaction.asset_id] || []).push(
                transaction
              );
            }
            if (transaction.trade_in_asset_id) {
              (acc[transaction.trade_in_asset_id] =
                acc[transaction.trade_in_asset_id] || []).push({
                ...transaction,
                amount: transaction.trade_in_amount,
                asset_symbol: transaction.trade_in_asset_symbol,
                asset_icon: transaction.trade_in_asset_icon,
              });
            }
            return acc;
          },
          {}
        );

        // Calculate total for each asset
        for (const asset_id in transactions_grouped) {
          const transactions = transactions_grouped[asset_id];
          const total = transactions.reduce(
            (acc, transaction) => acc + parseFloat(transaction.amount),
            0
          );

          if (asset_id) {
            items.push({
              asset_id: asset_id,
              asset_symbol: transactions[0].asset_symbol,
              asset_icon: transactions[0].asset_icon,
              total: total,
              total_formatted: formatter_asset(total, transactions[0].asset_symbol),
            });
          }
        }

        if (items.length > 0) {
          show = true;
        }
      }

      // Calculate total fiat value
      let fiat_amount_total = 0;
      this.transactions_selected.forEach(transaction => {
        // skip if trade_in_id is not null (to exclude trades)
        if (transaction.trade_in_id) {
          return;
        }
        // skip empty values
        if (transaction.fiat_value == void 0) {
          return;
        }
        fiat_amount_total += parseFloat(transaction.fiat_value);
      });
      const fiat_amount_total_formatted = formatter_fiat(fiat_amount_total);

      return {
        show: show,
        items: items,
        fiat_amount_total: fiat_amount_total,
        fiat_amount_total_formatted: fiat_amount_total_formatted,
      };
    },
  },
  watch: {
    search_params: {
      handler: function (new_value) {
        this.state_search_params = structuredClone(toRaw(new_value));
      },
      immediate: true,
    },
    transactions_filters(new_value) {
      this.state_transactions_filters = structuredClone(toRaw(new_value));
    },
    transactions(new_value) {
      this.state_transactions_meta = structuredClone(toRaw(new_value.meta));
    },
    'wac.polling': {
      handler: function (new_value, old_value) {
        if (new_value) {
          this.wac_polling_start();
        } else {
          if (old_value) {
            this.$inertia.get('');
          }
          this.wac_polling_stop();
        }
      },
      immediate: true,
    },
    'transactions_details.dialog': {
      handler: function () {
        this.counter_value_dialog_show = this.transaction_details?.dialog == 'counter_value';
      },
      immediate: true,
    },
    transaction_details(newValue) {
      if (!this.pending_accounting_details_movement_id) {
        return;
      }
      if (newValue?.movement_id !== this.pending_accounting_details_movement_id) {
        return;
      }
      this.pending_accounting_details_movement_id = null;
      this.$nextTick(() => {
        const sidebar = this.$refs.transactionsDetailsSidebar;
        if (sidebar?.openAccountingDetails) {
          sidebar.openAccountingDetails();
        }
      });
    },
    isTransactionDetailsSidebarOpen: {
      handler: function (isOpen) {
        if (typeof window === 'undefined') {
          return;
        }
        window.dispatchEvent(
          new CustomEvent('transaction-details-sidebar-toggle', { detail: { open: isOpen } })
        );
      },
      immediate: true,
    },
  },
  methods: {
    handleOnboardingFinish() {
      if (this.$refs.wrapper && typeof this.$refs.wrapper.finish === 'function') {
        this.$refs.wrapper.finish();
      }
    },
    openAccountingDetailsDialog(transaction) {
      if (!this.shared?.workspace?.is_business) {
        return;
      }
      if (!transaction) {
        return;
      }
      const movementId = transaction.movement_id || transaction.id || transaction.trade_in_id;
      if (!movementId) {
        return;
      }
      const openDialog = () => {
        const sidebar = this.$refs.transactionsDetailsSidebar;
        if (sidebar?.openAccountingDetails) {
          sidebar.openAccountingDetails();
        }
      };
      if (this.transaction_details?.movement_id === movementId) {
        this.transaction_details_section_show = true;
        this.$nextTick(openDialog);
        return;
      }
      this.pending_accounting_details_movement_id = movementId;
      this.transactions_details_sidebar_open(movementId);
    },
    buildTransactionContextMenuItems(transaction) {
      if (!transaction) {
        return [];
      }

      const buildAction = (key, label, icon, handler) => ({
        type: 'action',
        key,
        label,
        icon,
        onClick: handler,
      });

      const items = [
        buildAction(
          'copy',
          this.i18n.transaction_context_copy,
          'content_copy',
          () => this.copyTransactionInfo(transaction)
        ),
      ];

      const cumpValue = this.getCumpValue(transaction);
      const cumpLabel = this.i18n.transaction_context_copy_cump;
      if (cumpLabel && cumpValue !== undefined && cumpValue !== null && cumpValue !== '') {
        items.push(
          buildAction(
            'copy-cump',
            cumpLabel,
            'las la-calculator',
            () => this.copyCump(transaction)
          )
        );
      }

      const appendGroup = (group) => {
        if (group.length === 0) {
          return;
        }
        if (items.length > 0 && items[items.length - 1].type !== 'separator') {
          items.push({
            type: 'separator',
            key: `sep-${items.length}`,
          });
        }
        items.push(...group);
      };

      const filterIcon = 'sym_o_filter_list';
      const filterActions = [];

      if (transaction.kind) {
        filterActions.push(
          buildAction(
            'filter-kind',
            this.i18n.transaction_context_filter_kind,
            filterIcon,
            () => this.filterTransactionByKind(transaction)
          )
        );
      }

      filterActions.push(
        buildAction(
          'filter-date',
          this.i18n.transaction_context_filter_date,
          filterIcon,
          () => this.filterTransactionByDate(transaction)
        )
      );

      if (transaction.asset_id || transaction.exchange_asset_id) {
        filterActions.push(
          buildAction(
            'filter-asset',
            this.i18n.transaction_context_filter_asset,
            filterIcon,
            () => this.filterTransactionByAsset(transaction)
          )
        );
      }

      if (transaction.to_address) {
        filterActions.push(
          buildAction(
            'filter-recipient',
            this.i18n.transaction_context_filter_recipient,
            filterIcon,
            () => this.filterTransactionByAddress(transaction, 'to_address')
          )
        );
      }

      if (transaction.from_address) {
        filterActions.push(
          buildAction(
            'filter-sender',
            this.i18n.transaction_context_filter_sender,
            filterIcon,
            () => this.filterTransactionByAddress(transaction, 'from_address')
          )
        );
      }

      if (transaction.amount !== undefined && transaction.amount !== null) {
        filterActions.push(
          buildAction(
            'filter-amount',
            this.i18n.transaction_context_filter_amount,
            filterIcon,
            () => this.filterTransactionByAmount(transaction)
          )
        );
      }

      if (transaction.blockchain_txid || transaction.trade_in_blockchain_txid || transaction.trade_out_blockchain_txid) {
        filterActions.push(
          buildAction(
            'filter-hash',
            this.i18n.transaction_context_filter_hash,
            filterIcon,
            () => this.filterTransactionByHash(transaction)
          )
        );
      }

      appendGroup(filterActions);

      const editActions = [];

      editActions.push(
        buildAction(
          'edit-time',
          this.i18n.transaction_actions_edit_time,
          'sym_o_edit_calendar',
          () => this.openEditTimeDialog(transaction)
        )
      );

      editActions.push(
        buildAction(
          'edit-amount',
          this.i18n.transaction_actions_edit_amount,
          'sym_o_edit',
          () => this.editAmountDialog(transaction)
        )
      );

      editActions.push(
        buildAction(
          'edit-counter-value',
          this.i18n.transaction_details_counter_value_edit,
          'sym_o_paid',
          () => this.counter_value_dialog_open(transaction)
        )
      );

      if (this.shared?.workspace?.is_business) {
        editActions.push(
          buildAction(
            'accounting-details',
            this.i18n.transaction_details_title,
            'sym_o_list_alt_check',
            () => this.openAccountingDetailsDialog(transaction)
          )
        );
      }

      appendGroup(editActions);

      const duplicateActions = [
        buildAction(
          'duplicate',
          this.i18n.transaction_actions_duplicate,
          'content_copy',
          () => this.duplicateMovement(transaction.movement_id, transaction)
        ),
      ];

      appendGroup(duplicateActions);

      const destructiveActions = [];

      if (transaction.manual) {
        destructiveActions.push(
          buildAction(
            'delete',
            this.i18n.transaction_details_overview_delete_action,
            'sym_o_delete',
            () => this.transactions_delete_confirm(transaction)
          )
        );
      }

      appendGroup(destructiveActions);

      return items;
    },
    create_categorization_rule_dialog_open: function () {
      this.create_categorization_rule_form = {
        name: '',
        transaction_kind_id: null,
      };
      this.create_categorization_rule_dialog_show = true;
    },
    build_categorization_rule_filters: function () {
      const filters = {};

      if (this.state_search_params.exchange_account_id && this.state_search_params.exchange_account_id.length > 0) {
        filters.exchange_account_id = this.state_search_params.exchange_account_id;
      }

      if (this.state_search_params.asset_id && this.state_search_params.asset_id.length > 0) {
        filters.asset_id = this.state_search_params.asset_id;
      }

      if (this.state_search_params.kind && this.state_search_params.kind.length > 0) {
        filters.kind = this.state_search_params.kind;
      }

      if (this.state_search_params.uncategorized == 'true') {
        filters.uncategorized = this.state_search_params.uncategorized;
      }

      if (this.state_search_params.date_from || this.state_search_params.date_to) {
        if (this.state_search_params.date_from) filters.date_from = this.state_search_params.date_from;
        if (this.state_search_params.date_to) filters.date_to = this.state_search_params.date_to;
      }

      if (this.state_search_params.change_rate) {
        filters.change_rate = this.state_search_params.change_rate;
      }

      if (this.state_search_params.type) {
        filters.type = this.state_search_params.type;
      }

      if (this.state_search_params.manual !== undefined && this.state_search_params.manual !== null && this.state_search_params.manual !== '') {
        filters.manual = this.state_search_params.manual;
      }

      if (this.state_search_params.amount_min) {
        filters.amount_min = this.state_search_params.amount_min;
      }

      if (this.state_search_params.amount_max) {
        filters.amount_max = this.state_search_params.amount_max;
      }

      if (this.state_search_params.from_address) {
        filters.from_address = this.state_search_params.from_address;
      }

      if (this.state_search_params.to_address) {
        filters.to_address = this.state_search_params.to_address;
      }

      if (this.state_search_params.note) {
        filters.note = this.state_search_params.note;
      }

      if (this.state_search_params.exchange_asset_id && this.state_search_params.exchange_asset_id.length > 0) {
        filters.exchange_asset_id = this.state_search_params.exchange_asset_id;
      }

      return filters;
    },
    create_categorization_rule_submit: async function () {
      if (!this.create_categorization_rule_form.name || !this.create_categorization_rule_form.transaction_kind_id) {
        this.$q.notify({
          message: 'Veuillez remplir tous les champs requis',
          type: 'warning',
        });
        return;
      }

      this.create_categorization_rule_loading = true;

      console.log('state_search_params:', this.state_search_params);

      const filters = this.build_categorization_rule_filters();

      console.log('Filters to save:', filters);

      try {
        await this.$rails.post(
          `/w/${this.shared.workspace.id}/categorization_rules`,
          {
            categorization_rule: {
              name: this.create_categorization_rule_form.name,
              transaction_kind_id: this.create_categorization_rule_form.transaction_kind_id,
              filters: filters,
              active: true
            }
          }
        );

        this.$q.notify({
          message: 'Règle créée avec succès',
          type: 'positive',
        });

        this.create_categorization_rule_dialog_show = false;
        this.categorization_rules_load();
      } catch (error) {
        this.$q.notify({
          message: error.response?.data?.error || 'Erreur lors de la création de la règle',
          type: 'negative',
        });
      } finally {
        this.create_categorization_rule_loading = false;
      }
    },
    async categorization_rules_load() {
      try {
        const response = await fetch(`/w/${this.shared.workspace.id}/categorization_rules`, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
          }
        });
        const data = await response.json();
        this.categorization_rules = data.categorization_rules;
      } catch (error) {
        console.error('Error loading rules:', error);
      }
    },
    categorization_rule_preview(rule) {
      // Appliquer les filtres de la règle aux filtres de la page
      const filters = rule.filters;
      const newParams = {};

      if (filters.exchange_account_id) newParams.exchange_account_id = filters.exchange_account_id;
      if (filters.asset_id) newParams.asset_id = filters.asset_id;
      if (filters.kind) newParams.kind = filters.kind;
      if (filters.uncategorized) newParams.uncategorized = filters.uncategorized;
      if (filters.date_from) newParams.date_from = filters.date_from;
      if (filters.date_to) newParams.date_to = filters.date_to;
      if (filters.change_rate) newParams.change_rate = filters.change_rate;
      if (filters.type) newParams.type = filters.type;
      if (filters.manual !== undefined) newParams.manual = filters.manual;
      if (filters.amount_min) newParams.amount_min = filters.amount_min;
      if (filters.amount_max) newParams.amount_max = filters.amount_max;
      // from_address et to_address : convertir array en string si nécessaire
      if (filters.from_address) {
        newParams.from_address = Array.isArray(filters.from_address) ? filters.from_address[0] : filters.from_address;
      }
      if (filters.to_address) {
        newParams.to_address = Array.isArray(filters.to_address) ? filters.to_address[0] : filters.to_address;
      }
      if (filters.note) newParams.note = filters.note;
      if (filters.exchange_asset_id) newParams.exchange_asset_id = filters.exchange_asset_id;

      this.transactions_get(this.transactions_filters.action_path, newParams);

      this.$q.notify({
        message: `${this.i18n.categorization_rule_preview_notification}${rule.name}`,
        type: 'info',
      });
    },
    async categorization_rule_apply(id) {
      this.$q.dialog({
        title: 'Confirmer',
        message: 'Appliquer cette règle aux transactions ?',
        cancel: true,
        persistent: true
      }).onOk(async () => {
        try {
          const response = await fetch(`/w/${this.shared.workspace.id}/categorization_rules/${id}/apply`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            },
            body: JSON.stringify({})
          });
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Erreur lors de l\'application');
          }

          this.$q.notify({
            type: 'positive',
            message: data.message
          });

          // Recharger les transactions
          this.transactions_get(this.transactions_filters.action_path, this.state_search_params);
        } catch (error) {
          console.error(error);
          this.$q.notify({
            type: 'negative',
            message: error.message || "Erreur lors de l'application"
          });
        }
      });
    },
    async categorization_rule_delete(id) {
      this.$q.dialog({
        title: 'Confirmer',
        message: 'Voulez-vous vraiment supprimer cette règle ?',
        cancel: true,
        persistent: true
      }).onOk(async () => {
        try {
          const response = await fetch(`/w/${this.shared.workspace.id}/categorization_rules/${id}`, {
            method: 'DELETE',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            }
          });
          const data = await response.json();
          this.$q.notify({ type: 'positive', message: data.message });
          this.categorization_rules_load();
        } catch (error) {
          console.error(error);
          this.$q.notify({ type: 'negative', message: 'Erreur lors de la suppression' });
        }
      });
    },
    async categorization_rules_apply_all() {
      this.$q.dialog({
        title: 'Confirmer',
        message: 'Appliquer toutes les règles actives aux transactions ?',
        cancel: true,
        persistent: true
      }).onOk(async () => {
        try {
          const response = await fetch(`/w/${this.shared.workspace.id}/categorization_rules/apply_all`, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            },
            body: JSON.stringify({})
          });
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Erreur lors de l\'application');
          }

          this.$q.notify({
            type: 'positive',
            message: data.message
          });

          // Recharger les transactions
          this.transactions_get(this.transactions_filters.action_path, this.state_search_params);
        } catch (error) {
          console.error(error);
          this.$q.notify({
            type: 'negative',
            message: error.message || "Erreur lors de l'application"
          });
        }
      });
    },
    copyBalanceToClipboard: async function (balance) {
      if (balance === null || balance === undefined) {
        return;
      }
      try {
        await copyToClipboard(String(balance));
        this.$q.notify({
          message: this.i18n.copied,
          type: "positive",
          icon: "las la-clipboard-check",
          timeout: 1000,
        });
      } catch (error) {
        console.error('Failed to copy:', error);
        this.$q.notify({
          message: this.i18n.copied_error,
          type: "negative",
          icon: "info",
          timeout: 1000,
        });
      }
    },
    formatAmount: function (amount, symbol) {
      if (!amount) return '';
      const formatted = parseFloat(amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
      });
      return `${formatted} ${symbol || ''}`;
    },
    address_mappings_map: function (address) {
      if (!address) {
        return "---";
      }

      const name = this.address_mappings[address];

      if (name) {
        return name;
      } else {
        if (address.length > 16) {
          return (
            address.substring(0, 6) +
            "[...]" +
            address.substring(address.length - 8, address.length)
          );
        } else {
          return address;
        }
      }
    },
    transactions_filters_clear: function () {
      this.transactions_loading = true;
      this.state_search_params = {};

      this.transactions_get(
        this.transactions_filters.action_path,
        this.state_search_params,
      );
    },
    wac_update: function () {
      this.$inertia.post(
        this.wac.update_action,
        {},
        {
          preserveState: true,
          preserveScroll: true,
          onStart: () => {
          },
          onSuccess: () => {
          },
          onError: (errors) => {
            console.log(errors);
            this.form_errors = errors;
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          },
        }
      );
    },
    supporting_documents_download: function () {
      // TODO: In supporting_documents_download(), replace the ObjectURL creation by a temporary link coming from the backend.
      this.$rails.post(
        this.supporting_documents_download_all_action_path,
        { responseType: "blob" }
      ).then((response) => {
        if (!response.ok) {
          throw new Error(this.i18n.supporting_documents_download_error);
        }
        return response.blob();
      }).then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", this.i18n.supporting_documents_download_filename);
        document.body.appendChild(link);
        link.click();
      }).catch((error) => {
        console.error(error);
        this.$q.notify({
          message: this.i18n.supporting_documents_download_error,
          type: "negative",
        });
      });
    },
    transactions_kind_dialog_open: function (transaction) {
      this.transactions_kind_dialog_transaction = transaction;
      this.transactions_kind_dialog_show = true;
    },
    transactions_kind_dialog_select: function (kind) {
      const transaction = this.transactions_kind_dialog_transaction;
      let params = {};
      params.id = [transaction.id];
      if (kind == 'uncategorized') {
        params.kind = kind;
        params.justified = false;
      } else {
        params.kind = kind.id;
      }

      this.$inertia.post(
        this.transactions_update_by_id_action_path,
        params,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'errors',
            'flash',
            'wac',
            'search_params',
            'transactions_counts',
            'transaction_details',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
            this.transactions_kind_dialog_show = false;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          }
        }
      );
    },
    bulk_edit_close: function () {
      this.transactions_all_pages_selected = false;
      this.transactions_selection = [];
    },
    bulk_edit_kind_select: function (kind) {
      if (this.transactions_all_pages_selected) {
        this.bulk_edit_kind_select_by_search(kind);
      } else {
        this.bulk_edit_kind_select_by_id(kind);
      }
      // Clear Selection (https://github.com/ComptaCrypto/comptacrypto-back/pull/267)
      this.bulk_edit_close();
    },
    bulk_edit_kind_select_by_search: function (kind) {
      const params = {
        ...this.state_search_params,
        update: {
          kind: kind.id,
          justified: true,
        }
      };

      if (kind == 'uncategorized') {
        params.update.kind = kind;
        params.update.justified = false;
      }

      this.$inertia.post(
        this.transactions_update_by_search_action_path,
        params,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transactions_counts',
            'errors',
            'flash',
            'transaction_details',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          }
        }
      );
    },
    bulk_edit_kind_select_by_id: function (kind) {
      let params = {};
      params.id = this.transactions_selected.map(t => t.id);
      if (kind == 'uncategorized') {
        params.kind = kind;
        params.justified = false;
      } else {
        params.kind = kind.id;
      }
      this.$inertia.post(
        this.transactions_update_by_id_action_path,
        params,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transactions_counts',
            'errors',
            'flash',
            'wac',
            'search_params',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          }
        }
      );
    },
    bulk_edit_delete_confirm: function () {
      this.$q.dialog({
        title: this.i18n.bulk_edit_delete_dialog_title,
        message: this.i18n.bulk_edit_delete_dialog_message,
        cardClass: "whitespace-pre-line",
        cancel: true,
        color: "primary",
        persistent: true,
      }).onOk(() => {
        this.bulk_edit_delete();
      });
    },
    bulk_edit_delete: function () {
      if (this.transactions_all_pages_selected) {
        this.bulk_edit_delete_by_search();
      } else {
        this.bulk_edit_delete_by_id();
      }
      this.bulk_edit_close();
    },
    bulk_edit_delete_by_search: function () {
      const params = {
        ...this.state_search_params,
      };

      this.$inertia.post(
        this.transactions_delete_by_search_action_path,
        params,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transactions_counts',
            'errors',
            'flash',
            'transaction_details',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          }
        }
      );
    },
    bulk_edit_delete_by_id: function () {
      let params = {};
      params.id = this.transactions_selected.map(t => t.id);
      this.$inertia.post(
        this.transactions_delete_by_id_action_path,
        params,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transactions_counts',
            'errors',
            'flash',
            'wac',
            'search_params',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
          }
        }
      );
    },
    transactions_missing_change_rate_dialog_open: function (transaction) {
      this.transactions_missing_change_rate_dialog_transaction = transaction;
      this.transactions_missing_change_rate_dialog_show = true;
    },
    transactions_missing_change_rate_dialog_manual_update: function () {
      const tx = this.transactions_missing_change_rate_dialog_transaction;
      this.transactions_missing_change_rate_dialog_show = false;
      this.counter_value_dialog_open(tx);
      this.transactions_details_sidebar_open(tx.movement_id);
    },
    transactions_add_dialog_open: function () {
      this.transactions_add_dialog_loading = false;
      this.transactions_add_dialog_show = true;
      this.transactions_add_dialog_form = {};
      this.transactions_add_dialog_form.type = this.transactions_add_dialog_form_types[0];
      this.transactions_add_dialog_form_transaction_in_asset_error = false;
      this.transactions_add_dialog_form_transaction_out_asset_error = false;
      this.transactions_add_dialog_form_sender_show = false;
      this.transactions_add_dialog_form_recipient_show = false;
      this.transactions_assets_add_dialog_show = false;
      this.transactions_assets_add_dialog_loading = false;
      this.transactions_assets_add_form = {};
    },
    transactions_add_submit: function () {
      if ((
        this.transactions_add_dialog_form.type.value == 'credit' ||
        this.transactions_add_dialog_form.type.value == 'swap'
      ) && !this.transactions_add_dialog_form.transaction_in_asset) {
        this.transactions_add_dialog_form_transaction_in_asset_error = true;
        return;
      }
      if ((
        this.transactions_add_dialog_form.type.value == 'debit' ||
        this.transactions_add_dialog_form.type.value == 'swap'
      ) && !this.transactions_add_dialog_form.transaction_out_asset) {
        this.transactions_add_dialog_form_transaction_out_asset_error = true;
        return;
      }

      this.$inertia.post(
        this.transaction_create_action_path,
        {
          date: this.transactions_add_dialog_form.date,
          time: this.transactions_add_dialog_form.time,
          from_address: this.transactions_add_dialog_form.from_address,
          to_address: this.transactions_add_dialog_form.to_address,
          type: this.transactions_add_dialog_form.type.value,
          transaction_in_amount: this.transactions_add_dialog_form.transaction_in_amount,
          transaction_in_asset_id: this.transactions_add_dialog_form.transaction_in_asset?.id,
          transaction_out_amount: this.transactions_add_dialog_form.transaction_out_amount,
          transaction_out_asset_id: this.transactions_add_dialog_form.transaction_out_asset?.id,
          exchange_account_id: this.transactions_add_dialog_form.exchange_account.id,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transactions_counts',
            'errors',
            'flash',
            'wac',
            'search_params',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
            this.transactions_add_dialog_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
            this.transactions_add_dialog_show = false;
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.transactions_add_dialog_loading = false;
          }
        }
      );
    },
    transactions_add_exchange_assets_dialog_open: function (in_or_out) {
      this.transactions_assets_add_direction = in_or_out;
      this.transactions_assets_add_dialog_show = true;
    },
    transactions_add_exchange_assets_search: function (new_value) {
      this.$inertia.get(
        '',
        {
          transactions_add_exchange_assets_search_exchange_name: this.transactions_add_dialog_form.exchange_account?.exchange_name,
          transactions_add_exchange_assets_search_query: new_value,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transactions_add_exchange_assets'
          ],
          onStart: () => {
          },
          onSuccess: () => {
          },
          onError: (errors) => {
            console.error(errors);
          },
          onFinish: () => {
          }
        }
      );
    },
    transactions_add_exchange_asset_create: function () {
      this.$inertia.post(
        this.exchange_asset_create_action_path,
        {
          exchange_name: this.transactions_add_dialog_form.exchange_account?.exchange_name,
          exchange_symbol: this.transactions_assets_add_form.exchange_symbol,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'errors',
            'flash',
            'transactions_add_exchange_assets_selected'
          ],
          onStart: () => {
            this.transactions_assets_add_dialog_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
            if (this.transactions_assets_add_direction == 'in') {
              this.transactions_add_dialog_form.transaction_in_asset = this.transactions_add_exchange_assets_selected;
            } else {
              this.transactions_add_dialog_form.transaction_out_asset = this.transactions_add_exchange_assets_selected;
            }
            this.transactions_assets_add_dialog_show = false;
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.transactions_assets_add_dialog_loading = false;
          }
        }
      );
    },
    transactions_pagination_update: function (url, params) {
      let meta = this.transactions.meta;
      this.state_transactions_meta.page = params.page;
      this.state_transactions_meta.limit = params.per_page;
      this.state_transactions_meta.next = Math.min(params.page + 1, meta.last);
      this.state_transactions_meta.prev = Math.max(params.page - 1, 1);
      this.transactions_filter_add(params);
    },
    transactions_page_select: function (checked) {
      if (checked) {
        this.transactions.data.forEach((t) => {
          this.transactions_selection[t.id] = checked;
        });
      } else {
        this.transactions_all_pages_selected = false;
        this.transactions.data.forEach((t) => {
          delete this.transactions_selection[t.id];
        });
      }
    },
    transactions_select: function (t, checked) {
      if (checked) {
        this.transactions_selection[t.id] = checked;
      } else {
        this.transactions_all_pages_selected = false;
        delete this.transactions_selection[t.id];
      }
    },
    transactions_filter_add: function (search_param) {
      this.transactions_loading = true;
      this.state_search_params = {
        ...this.state_search_params,
        ...search_param,
      };

      this.transactions_get(
        this.transactions_filters.action_path,
        this.state_search_params,
      );
    },
    filter_by_exact_date: function (transaction, overrides = {}) {
      const applyDateFilter = (dateStr) => {
        if (!dateStr) return false;
        const parts = dateStr.split('/');
        if (parts.length !== 3) return false;

        let [day, month, year] = parts;
        if (!day || !month || !year) return false;

        if (year.length === 2) {
          year = '20' + year;
        }

        const formatted = `${year}/${month}/${day}`;

        this.transactions_filter_add({
          date_from: formatted,
          date_to: formatted
        });
        return true;
      };

      const applyDateTimeFilter = (dateTimeStr) => {
        if (!dateTimeStr) return false;
        const [datePart] = dateTimeStr.split(' ');
        return applyDateFilter(datePart);
      };

      if (applyDateTimeFilter(overrides.datetime)) {
        return;
      }

      if (applyDateFilter(overrides.date)) {
        return;
      }

      if (!transaction) {
        return;
      }

      if (!transaction.datetime) {
        applyDateFilter(transaction.date);
        return;
      }

      applyDateTimeFilter(transaction.datetime);
    },
    async copyTransactionInfo(transaction) {
      if (!transaction) {
        return;
      }
      const lines = [];
      const addLine = (label, value) => {
        if (value === undefined || value === null || value === '') {
          return;
        }
        lines.push(`${label}: ${value}`);
      };
      const formatActor = (address, exchangeAccount) => {
        if (address && exchangeAccount?.name) {
          return `${address} (${exchangeAccount.name})`;
        }
        return address || exchangeAccount?.name || '';
      };

      const formatPlainAmount = (value) => {
        if (value === undefined || value === null || value === '') {
          return '';
        }
        const num = Number(value);
        if (!Number.isFinite(num)) {
          return value;
        }
        return num.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 8,
        });
      };
      const stripSymbol = (value) => {
        if (!value) {
          return '';
        }
        const symbol = transaction.asset_symbol || transaction.exchange_asset_symbol;
        if (!symbol) {
          return value;
        }
        const regex = new RegExp(`\\s*${symbol.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`);
        return value.replace(regex, '').trim();
      };

      const amountValue = formatPlainAmount(transaction.amount) || stripSymbol(transaction.amount_formatted);
      addLine(this.i18n.transaction_context_amount_label, amountValue);

      const assetName = transaction.asset_name || transaction.exchange_asset_name;
      const assetSymbol = transaction.asset_symbol || transaction.exchange_asset_symbol;
      let assetValue;
      if (assetName && assetSymbol) {
        assetValue = `${assetName} (${assetSymbol})`;
      } else {
        assetValue = assetName || assetSymbol || '';
      }
      addLine(this.i18n.transaction_context_asset_label, assetValue);

      addLine(this.i18n.transaction_context_fiat_label, transaction.fiat_value_formatted);

      const dateTime = [transaction.date, transaction.time].filter(Boolean).join(' ');
      addLine(this.i18n.transaction_context_date_label, dateTime);

      const sender = formatActor(transaction.from_address, transaction.from_exchange_account);
      addLine(this.i18n.transaction_context_sender_label, sender);

      const recipient = formatActor(transaction.to_address, transaction.to_exchange_account);
      addLine(this.i18n.transaction_context_recipient_label, recipient);

      addLine(this.i18n.transaction_context_kind_label, transaction.kind_label);
      addLine(this.i18n.transaction_context_note_label, transaction.note);
      addLine(this.i18n.transaction_context_id_label, transaction.movement_id);
      addLine(this.i18n.transaction_context_txhash_label, transaction.blockchain_txid);

      if (lines.length === 0 && transaction.summary) {
        let plainSummary;
        if (typeof window !== 'undefined' && window?.document) {
          const tempEl = document.createElement('div');
          tempEl.innerHTML = transaction.summary;
          plainSummary = tempEl.textContent || tempEl.innerText || '';
        } else {
          plainSummary = transaction.summary.replace(/<[^>]+>/g, '');
        }
        if (plainSummary.trim()) {
          lines.push(plainSummary.trim());
        }
      }
      const summary = lines.join('\n');
      if (!summary.trim()) {
        return;
      }
      try {
        await copyToClipboard(summary);
        this.$q.notify({
          message: this.i18n.copied,
          type: "positive",
          icon: "las la-clipboard-check",
          timeout: 1000,
        });
      } catch (error) {
        console.error('Failed to copy transaction summary', error);
        this.$q.notify({
          message: this.i18n.copied_error,
          type: "negative",
          icon: "warning",
          timeout: 1000,
        });
      }
    },
    getCumpValue(transaction) {
      if (!transaction) {
        return null;
      }
      const candidates = [
        transaction.wac_full,
        transaction.wac,
        transaction.trade_in_wac_full,
        transaction.trade_in_wac,
      ];
      for (const val of candidates) {
        if (val === undefined || val === null || val === '') {
          continue;
        }
        return val;
      }
      return null;
    },
    async copyCump(transaction) {
      const value = this.getCumpValue(transaction);
      if (value === null || value === undefined || value === '') {
        return;
      }
      try {
        await copyToClipboard(String(value));
        this.$q.notify({
          message: this.i18n.copied,
          type: "positive",
          icon: "las la-clipboard-check",
          timeout: 1000,
        });
      } catch (error) {
        console.error('Failed to copy CUMP', error);
        this.$q.notify({
          message: this.i18n.copied_error,
          type: "negative",
          icon: "warning",
          timeout: 1000,
        });
      }
    },
    filterTransactionByKind(transaction) {
      if (!transaction?.kind) {
        return;
      }
      this.transactions_filter_add({
        kind: [transaction.kind],
        uncategorized: undefined,
      });
    },
    filterTransactionByDate(transaction) {
      this.filter_by_exact_date(transaction);
    },
    filterTransactionByAsset(transaction) {
      if (transaction?.asset_id) {
        this.transactions_filter_add({
          asset_id: [transaction.asset_id],
          exchange_asset_id: undefined,
        });
        return;
      }
      if (transaction?.exchange_asset_id) {
        this.transactions_filter_add({
          exchange_asset_id: [transaction.exchange_asset_id],
          asset_id: undefined,
        });
      }
    },
    filterTransactionByAddress(transaction, field) {
      if (!transaction || !field) {
        return;
      }
      const value = transaction[field];
      if (!value) {
        return;
      }
      this.transactions_filter_add({
        [field]: value,
      });
    },
    filterTransactionByAmount(transaction) {
      if (transaction?.amount === undefined || transaction?.amount === null) {
        return;
      }
      this.transactions_filter_add({
        amount_min: transaction.amount,
        amount_max: transaction.amount,
      });
    },
    filterTransactionsByHashValue(hash) {
      if (!hash) {
        return;
      }
      this.transactions_filter_add({
        id: hash,
      });
    },
    filterTransactionByHash(transaction) {
      if (!transaction) {
        return;
      }
      const hash = transaction.blockchain_txid || transaction.trade_in_blockchain_txid || transaction.trade_out_blockchain_txid;
      this.filterTransactionsByHashValue(hash);
    },
    transactions_filter_uncategorized_toggle: function () {
      let value;
      if (this.state_search_params.uncategorized == 'true') {
        value = undefined;
      } else {
        value = 'true';
      }
      this.transactions_filter_add({ uncategorized: value });
    },
    transactions_filter_replace_all: function (search_param) {
      this.transactions_loading = true;
      this.state_search_params = search_param;

      this.transactions_get(
        this.transactions_filters.action_path,
        this.state_search_params,
      );
    },
    transactions_update: function (transaction_details, params) {
      this.$inertia.put(
        `transactions/${transaction_details.movement_id}`,
        params,
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transactions_counts',
            'errors',
            'flash',
            'wac',
            'transaction_details',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
            this.transactions_update_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.transactions_update_loading = false;
          }
        }
      );
    },
    transactions_delete_confirm: function (transaction_details) {
      this.$q.dialog({
        title: this.i18n.transaction_delete_dialog_title,
        message: this.i18n.transaction_delete_dialog_message,
        cardClass: "whitespace-pre-line",
        cancel: true,
        color: "primary",
        persistent: true,
      }).onOk(() => {
        this.transactions_delete(transaction_details);
      });
    },
    transactions_delete: function (transaction_details) {
      this.$inertia.post(
        this.transactions_delete_movement_action_path,
        { id: transaction_details.movement_id },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transactions_counts',
            'errors',
            'flash',
            'wac',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
            this.transactions_delete_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
            this.transaction_details_section_show = false;
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.transactions_delete_loading = false;
          }
        }
      );
    },
    transactions_get: function (url, params) {
      this.$inertia.get(
        url,
        params,
        {
          preserveState: true,
          preserveScroll: true,
          only: [
            'shared',
            'transactions',
            'search_params',
            'transactions_filters',
            'transactions_export_path',
          ],
          onStart: () => {
            this.transactions_loading = true;
          },
          onSuccess: () => {
          },
          onError: (errors) => {
            console.log(errors);
          },
          onFinish: () => {
            this.transactions_loading = false;
          },
        }
      );
    },
    transactions_details_sidebar_close: function () {
      this.transaction_details_section_show = false;
      this.pending_accounting_details_movement_id = null;
    },
    transactions_details_sidebar_open: function (movement_id) {
      this.transaction_details_section_show = true;
      this.$inertia.get(
        '',
        { transaction_details: movement_id },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'transaction_details'
          ],
          onStart: () => {
            this.transactions_details_sidebar_loading = true;
          },
          onError: (errors) => {
            console.error(errors);
          },
          onFinish: () => {
            this.transactions_details_sidebar_loading = false;
          }
        }
      );
    },
    bulk_edit_transactions_merge: function () {
      const ids = this.transactions_selected.map((t) => t.id);
      this.$inertia.post(
        this.transactions_trade_create_action_path,
        { movements: ids },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'errors',
            'flash',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
            this.bulk_edit_action_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
            // Clear Selection (https://github.com/ComptaCrypto/comptacrypto-back/pull/267) (hdorio: Please keep this comment)
            this.bulk_edit_close();
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.bulk_edit_action_loading = false;
          },
        }
      );
    },
    // Internal transfer action for 3+ selected movements of the same asset.
    // Sums every incoming and outgoing amount; the imbalance decides whether
    // it is a plain internal transfer, one with commission (sent > received)
    // or one with airdrop (received > sent).
    bulk_edit_internal_transfer_action_multi: function () {
      const txs = this.transactions_selected;

      // Only regular movements (not trade legs) can be internal transfers.
      const is_regular = txs.every(
        (t) => !t.trade_in_id && !t.trade_out_id
      );
      if (!is_regular) return null;

      // Every movement must share the same asset.
      const ref = txs[0];
      const all_same_asset = txs.every(
        (t) =>
          (ref.exchange_asset_id && t.exchange_asset_id && ref.exchange_asset_id === t.exchange_asset_id) ||
          (ref.asset_id != null && t.asset_id != null && ref.asset_id === t.asset_id)
      );
      if (!all_same_asset) return null;

      const total_in = txs
        .map((t) => parseFloat(t.amount))
        .filter((a) => a > 0)
        .reduce((sum, a) => sum + a, 0);
      const total_out = txs
        .map((t) => parseFloat(t.amount))
        .filter((a) => a < 0)
        .reduce((sum, a) => sum + Math.abs(a), 0);

      // Need at least one incoming and one outgoing movement.
      if (total_in === 0 || total_out === 0) return null;

      const difference = total_out - total_in;
      const epsilon = 1e-9;

      if (Math.abs(difference) < epsilon) {
        return {
          type: 'internal_transfer',
          label: this.i18n.bulk_edit_internal_transfer_action,
          action: 'internal_transfer',
          enabled: true,
          disabledReason: null,
        };
      } else if (difference > 0) {
        return {
          type: 'internal_transfer_with_commission',
          label: this.i18n.bulk_edit_internal_transfer_with_commission_action,
          action: 'internal_transfer',
          enabled: true,
          disabledReason: null,
        };
      } else {
        return {
          type: 'internal_transfer_with_airdrop',
          label: this.i18n.bulk_edit_internal_transfer_with_airdrop_action,
          action: 'internal_transfer',
          enabled: true,
          disabledReason: null,
        };
      }
    },
    bulk_edit_transactions_internal_transfer: function () {
      const ids = this.transactions_selected.map((t) => t.id);
      this.$inertia.post(
        this.transactions_internal_transfer_create_action_path,
        { movements: ids },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'errors',
            'flash',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
            this.bulk_edit_action_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
            this.bulk_edit_close();
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.bulk_edit_action_loading = false;
          }
        }
      );
    },
    bulk_edit_transactions_bridge: function () {
      const ids = this.transactions_selected.map((t) => t.id);
      this.$inertia.post(
        this.transactions_bridge_create_action_path,
        { movements: ids },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          only: [
            'shared',
            'errors',
            'flash',
            'transactions',
            'transactions_export_path',
          ],
          onStart: () => {
            this.bulk_edit_action_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
            this.bulk_edit_close();
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.bulk_edit_action_loading = false;
          }
        }
      );
    },
    $t(str, o) {
      console.log(str, o);
      return `missing: ${str}`;
    },
    missing_change_rate_html(symbol) {
      const escapedSymbol = String(symbol ?? '').replace(/[&<>"']/g, (character) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
      })[character]);
      const tmpl = `
        <div class="inline ellipsis max-w[25ch]">
          <strong>${escapedSymbol}</strong>
        </div>
      `;
      return this.i18n.transactions_warnings_missing_change_rate.replace("%{0}", tmpl);
    },
    address_mappings_dialog_open: function (address, transaction) {
      this.address_mappings_dialog_address = address;
      this.address_mappings_dialog_transaction = transaction;
      this.address_mappings_dialog_show = true;
      this.address_mappings_dialog_name = '';
      this.address_mappings_dialog_auxiliary_account_number = '';
    },
    address_mappings_dialog_submit: function () {
      this.$inertia.post(
        this.address_mappings_update_action_path,
        {
          address: this.address_mappings_dialog_address,
          name: this.address_mappings_dialog_name,
          auxiliary_account_number: this.address_mappings_dialog_auxiliary_account_number,
        },
        {
          preserveState: true,
          preserveScroll: true,
          replace: true,
          onStart: () => {
            this.address_mappings_dialog_loading = true;
          },
          onSuccess: () => {
            this.$q.notify({
              message: this.$page.props.flash.success,
              type: "positive",
            });
            this.address_mappings_dialog_show = false;
          },
          onError: (errors) => {
            console.error(errors);
            this.$q.notify({
              message: this.$page.props.flash.alert,
              type: "negative",
            });
          },
          onFinish: () => {
            this.address_mappings_dialog_loading = false;
          }
        }
      );
    },
    counter_value_dialog_open: function (transaction) {
      this.counter_value_dialog_transaction = transaction;
      this.counter_value_dialog_fiat_amount = transaction.fiat_amount;
      this.counter_value_dialog_show = true;
    },
    counter_value_dialog_submit: function () {
      if (this.counter_value_dialog_fiat_amount === null || this.counter_value_dialog_fiat_amount === '') {
        return;
      }
      this.counter_value_dialog_update(this.counter_value_dialog_fiat_amount);
    },
    counter_value_dialog_update: function (value) {
      this.transactions_update(this.counter_value_dialog_transaction, { fiat_amount: value });
      this.counter_value_dialog_show = false;
    },
    duplicateMovement: function (movementId, transaction = null) {
      // Duplicate with the same transaction kind as the original
      const transactionKindId = transaction?.kind || transaction?.kind_id || this.transaction_details?.kind_id;

      this.$inertia.post(
        `/w/${this.shared.workspace.id}/transactions/${movementId}/duplicate`,
        { transaction_kind_id: transactionKindId },
        {
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            // Refresh the transactions list with current filters
            this.transactions_get(
              this.transactions_filters.action_path,
              this.state_search_params
            );
            this.$q.notify({
              type: 'positive',
              message: this.i18n.transaction_actions_duplicate_success
            });
          },
          onError: () => {
            this.$q.notify({
              type: 'negative',
              message: this.i18n.transaction_actions_duplicate_error
            });
          }
        }
      );
    },

    openEditTimeDialog: function (transaction) {
      this.edit_time_dialog_transaction = transaction;
      const { date, time } = this.extractDateTimeStrings(transaction);
      this.edit_time_dialog_form = {
        date,
        time,
      };
      this.edit_time_dialog_has_original = !!this.getEditableOriginalTime(transaction);
      this.edit_time_dialog_loading = false;
      this.edit_time_dialog_show = true;
      this.$nextTick(() => {
        if (this.$refs.edit_time_dialog_form_ref) {
          this.$refs.edit_time_dialog_form_ref.resetValidation();
        }
      });
    },
    editTimeDialogUseOriginalTime: function () {
      if (!this.edit_time_dialog_has_original) {
        return;
      }
      const movementId = this.edit_time_dialog_transaction?.movement_id;
      if (!movementId) {
        return;
      }

      this.edit_time_dialog_loading = true;
      this.$inertia.post(
        `/w/${this.shared.workspace.id}/transactions/${movementId}/restore_time`,
        {},
        {
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            this.transactions_details_sidebar_open(movementId);
            this.transactions_get(
              this.transactions_filters.action_path,
              this.state_search_params
            );
            this.$q.notify({
              type: 'positive',
              message: this.i18n.transaction_actions_time_restored
            });
            this.closeEditTimeDialog();
          },
          onError: () => {
            this.$q.notify({
              type: 'negative',
              message: this.i18n.transaction_actions_restore_time_error
            });
          },
          onFinish: () => {
            this.edit_time_dialog_loading = false;
          }
        }
      );
    },
    closeEditTimeDialog: function () {
      this.edit_time_dialog_show = false;
    },
    resetEditTimeDialog: function () {
      this.edit_time_dialog_form = {
        date: '',
        time: '',
      };
      this.edit_time_dialog_has_original = false;
      this.edit_time_dialog_transaction = null;
      this.edit_time_dialog_loading = false;
      if (this.$refs.edit_time_dialog_form_ref) {
        this.$refs.edit_time_dialog_form_ref.resetValidation();
      }
    },
    submitEditTimeDialog: function () {
      const formRef = this.$refs.edit_time_dialog_form_ref;
      if (!formRef) {
        return;
      }
      formRef.validate().then((valid) => {
        if (!valid) {
          return;
        }
        const movementId = this.edit_time_dialog_transaction?.movement_id;
        if (!movementId) {
          return;
        }
        this.edit_time_dialog_loading = true;
        this.$inertia.post(
          `/w/${this.shared.workspace.id}/transactions/${movementId}/update_time`,
          {
            date: this.edit_time_dialog_form.date,
            time: this.edit_time_dialog_form.time,
          },
          {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
              this.transactions_details_sidebar_open(movementId);
              this.transactions_get(
                this.transactions_filters.action_path,
                this.state_search_params
              );
              this.$q.notify({
                type: 'positive',
                message: this.i18n.transaction_actions_time_updated
              });
              this.closeEditTimeDialog();
            },
            onError: () => {
              this.$q.notify({
                type: 'negative',
                message: this.i18n.transaction_actions_time_update_error
              });
            },
            onFinish: () => {
              this.edit_time_dialog_loading = false;
            }
          }
        );
      });
    },
    restoreOriginalTime: function (movementId) {
      this.$q.dialog({
        title: this.i18n.transaction_actions_restore_time_title,
        message: this.i18n.transaction_actions_restore_time_confirm,
        cancel: true,
        persistent: true
      }).onOk(() => {
        this.$inertia.post(
          `/w/${this.shared.workspace.id}/transactions/${movementId}/restore_time`,
          {},
          {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
              this.transactions_details_sidebar_open(movementId);
              this.transactions_get(
                this.transactions_filters.action_path,
                this.state_search_params
              );
              this.$q.notify({
                type: 'positive',
                message: this.i18n.transaction_actions_time_restored
              });
            },
            onError: () => {
              this.$q.notify({
                type: 'negative',
                message: this.i18n.transaction_actions_restore_time_error
              });
            }
          }
        );
      });
    },
    extractDateTimeStrings: function (transaction) {
      let dateStr = '';
      let timeStr = '';

      if (transaction?.datetime) {
        const [datePart, timePart] = transaction.datetime.split(' ');
        if (datePart) {
          const parts = datePart.split('/');
          if (parts.length === 3) {
            const [day, month, year] = parts;
            if (day && month && year) {
              dateStr = `${year}/${month}/${day}`;
            }
          }
        }
        if (timePart) {
          timeStr = timePart;
        }
      }

      const milliseconds = Number(transaction?.time_in_milliseconds);
      if ((!dateStr || !timeStr) && !Number.isNaN(milliseconds) && milliseconds > 0) {
        const dateObj = new Date(milliseconds);
        if (!Number.isNaN(dateObj.getTime())) {
          if (!dateStr) {
            dateStr = this.formatDateForInput(dateObj);
          }
          if (!timeStr) {
            timeStr = this.formatTimeForInput(dateObj);
          }
        }
      }

      return {
        date: dateStr,
        time: timeStr
      };
    },
    formatDateForInput: function (dateObj) {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    },
    formatTimeForInput: function (dateObj) {
      const hours = String(dateObj.getHours()).padStart(2, '0');
      const minutes = String(dateObj.getMinutes()).padStart(2, '0');
      const seconds = String(dateObj.getSeconds()).padStart(2, '0');
      return `${hours}:${minutes}:${seconds}`;
    },
    isTradeInEditFromTradeOutTransaction: function (transaction) {
      if (!transaction || transaction.trade_in_amount === undefined || !transaction.trade_in_id) {
        return false;
      }
      return String(transaction.movement_id) === String(transaction.trade_in_id);
    },
    getEditableOriginalTime(transaction) {
      if (this.isTradeInEditFromTradeOutTransaction(transaction)) {
        return transaction.trade_in_original_time;
      }
      return transaction?.original_time;
    },
    getEditableOriginalTimeFormatted: function (transaction) {
      if (this.isTradeInEditFromTradeOutTransaction(transaction)) {
        return transaction.trade_in_original_time_formatted;
      }
      return transaction?.original_time_formatted;
    },
    getEditableOriginalAmount: function (transaction) {
      if (this.isTradeInEditFromTradeOutTransaction(transaction)) {
        return transaction.trade_in_original_amount;
      }
      return transaction?.original_amount;
    },
    getEditableAmountToEdit: function (transaction) {
      if (this.isTradeInEditFromTradeOutTransaction(transaction)) {
        return transaction.trade_in_amount;
      }
      return transaction?.amount;
    },
    getEditableAmountSuffix: function (transaction) {
      if (this.isTradeInEditFromTradeOutTransaction(transaction)) {
        return transaction.trade_in_asset_symbol || transaction.trade_in_exchange_asset_symbol || '';
      }
      return transaction?.asset_symbol || transaction?.exchange_asset_symbol || '';
    },
    editAmountDialog: function (transaction) {
      this.edit_amount_dialog_transaction = transaction;
      const amountToEdit = this.getEditableAmountToEdit(transaction);
      this.edit_amount_dialog_reference_amount = amountToEdit ?? null;
      this.edit_amount_dialog_form = {
        amount: amountToEdit !== undefined && amountToEdit !== null ? String(amountToEdit) : '',
      };
      this.edit_amount_dialog_suffix = this.getEditableAmountSuffix(transaction);
      const originalAmount = this.getEditableOriginalAmount(transaction);
      this.edit_amount_dialog_original_amount = originalAmount ?? null;
      this.edit_amount_dialog_has_original = originalAmount !== undefined && originalAmount !== null;
      this.edit_amount_dialog_loading = false;
      this.edit_amount_dialog_show = true;
      this.$nextTick(() => {
        if (this.$refs.edit_amount_dialog_form_ref) {
          this.$refs.edit_amount_dialog_form_ref.resetValidation();
        }
      });
    },
    editAmountDialogUseOriginalAmount: function () {
      if (!this.edit_amount_dialog_has_original) {
        return;
      }
      const movementId = this.edit_amount_dialog_transaction?.movement_id;
      if (!movementId) {
        return;
      }

      this.edit_amount_dialog_loading = true;
      this.$inertia.post(
        `/w/${this.shared.workspace.id}/transactions/${movementId}/restore_amount`,
        {},
        {
          preserveState: true,
          preserveScroll: true,
          onSuccess: () => {
            this.transactions_details_sidebar_open(movementId);
            this.transactions_get(
              this.transactions_filters.action_path,
              this.state_search_params
            );
            this.$q.notify({
              type: 'positive',
              message: this.i18n.transaction_actions_amount_restored
            });
            this.closeEditAmountDialog();
          },
          onError: () => {
            this.$q.notify({
              type: 'negative',
              message: this.i18n.transaction_actions_restore_error
            });
          },
          onFinish: () => {
            this.edit_amount_dialog_loading = false;
          }
        }
      );
    },
    closeEditAmountDialog: function () {
      this.edit_amount_dialog_show = false;
    },
    resetEditAmountDialog: function () {
      this.edit_amount_dialog_form = {
        amount: '',
      };
      this.edit_amount_dialog_transaction = null;
      this.edit_amount_dialog_suffix = '';
      this.edit_amount_dialog_reference_amount = null;
      this.edit_amount_dialog_has_original = false;
      this.edit_amount_dialog_original_amount = null;
      this.edit_amount_dialog_loading = false;
      if (this.$refs.edit_amount_dialog_form_ref) {
        this.$refs.edit_amount_dialog_form_ref.resetValidation();
      }
    },
    submitEditAmountDialog: function () {
      const formRef = this.$refs.edit_amount_dialog_form_ref;
      if (!formRef) {
        return;
      }
      formRef.validate().then((valid) => {
        if (!valid) {
          return;
        }
        const movementId = this.edit_amount_dialog_transaction?.movement_id;
        if (!movementId) {
          return;
        }

        this.edit_amount_dialog_loading = true;
        this.$inertia.post(
          `/w/${this.shared.workspace.id}/transactions/${movementId}/update_amount`,
          { amount: this.edit_amount_dialog_form.amount },
          {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
              this.transactions_details_sidebar_open(movementId);
              this.transactions_get(
                this.transactions_filters.action_path,
                this.state_search_params
              );
              this.$q.notify({
                type: 'positive',
                message: this.i18n.transaction_actions_amount_updated
              });
              this.closeEditAmountDialog();
            },
            onError: () => {
              this.$q.notify({
                type: 'negative',
                message: this.i18n.transaction_actions_amount_update_error
              });
            },
            onFinish: () => {
              this.edit_amount_dialog_loading = false;
            }
          }
        );
      });
    },

    restoreOriginalAmount: function (movementId) {
      this.$q.dialog({
        title: this.i18n.transaction_actions_restore_title,
        message: this.i18n.transaction_actions_restore_confirm,
        cancel: true,
        persistent: true
      }).onOk(() => {
        this.$inertia.post(
          `/w/${this.shared.workspace.id}/transactions/${movementId}/restore_amount`,
          {},
          {
            preserveState: true,
            preserveScroll: true,
            onSuccess: () => {
              // Refresh the transaction details
              this.transactions_details_sidebar_open(movementId);
              // Refresh the transactions list with current filters
              this.transactions_get(
                this.transactions_filters.action_path,
                this.state_search_params
              );
              this.$q.notify({
                type: 'positive',
                message: this.i18n.transaction_actions_amount_restored
              });
            },
            onError: () => {
              this.$q.notify({
                type: 'negative',
                message: this.i18n.transaction_actions_restore_error
              });
            }
          }
        );
      });
    },
  },
};
</script>
<style scoped>
.highlighted {
  border-color: var(--q-accent);
}

tr:hover .reveal_on_hover {
  display: inline-block;
}

.reveal_on_hover {
  display: none;
}
</style>
<style>
.rule-filters-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rule-filter-chip {
  background-color: var(--select-filter-active-background) !important;
  border: 1px solid var(--select-filter-active-border) !important;
  color: var(--text-default);
}

.body--dark .rule-filter-chip {
  border-color: var(--select-filter-active-border);
}

.rule-filter-chip__value {
  display: inline-flex;
  align-items: center;
}

.transactions_kind_selector_panel {
  max-height: 70vh;
  min-width: 300px;
  width: 100%;
}

.transactions_kind_selector_content {
  display: flex;
  flex-direction: column;
  max-height: inherit;
}

.transactions_kind_selector_popup {
  display: block;
  max-height: 80vh !important;
  min-height: 1px;
  padding: 0 !important;
  overflow: hidden;
}
.transactions_kind_selector_scroll {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.transactions_kind_selector_header {
  position: sticky;
  top: 0;
  z-index: 1;
  padding-bottom: 0 !important;
}

.transactions_kind_selector_header .q-field {
  border-bottom: 1px solid #0d0d0d1a;
  border-radius: 0;
}

.body--dark .transactions_kind_selector_header .q-field {
  border-bottom: 1px solid #ffffff0d;
}

.transactions_kind_selector_header .q-field__native {
  outline: none;
  background-color: transparent;
}

.transactions_kind_selector_header .q-field__control {
  background-color: transparent !important;
  box-shadow: none !important;
  border-radius: 8px;
}

.transactions_kind_selector_header .q-field__control:before,
.transactions_kind_selector_header .q-field__control:after {
  border-color: transparent !important;
  box-shadow: none !important;
}

.modified-indicator {
  position: relative;
  display: inline-block;
  width: 0;
  height: 0;
  min-width: 0;
  vertical-align: middle;
}

.transactions_import_source_selector_panel {
  max-height: 70vh;
  min-width: 300px;
  width: 100%;
}

.transactions_import_source_selector_content {
  display: flex;
  flex-direction: column;
  max-height: inherit;
}

.transactions_import_source_selector_popup {
  display: block;
  max-height: 80vh !important;
  min-height: 1px;
  padding: 0 !important;
  overflow: hidden;
}

.transactions_import_source_selector_scroll {
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
}

.transactions_import_source_selector_header {
  position: sticky;
  top: 0;
  z-index: 1;
  padding-bottom: 0 !important;
}

.transactions_import_source_selector_header .q-field {
  border-bottom: 1px solid #0d0d0d1a;
  border-radius: 0;
}

.body--dark .transactions_import_source_selector_header .q-field {
  border-bottom: 1px solid #ffffff0d;
}

.transactions_import_source_selector_header .q-field__native {
  outline: none;
  background-color: transparent;
}

.transactions_import_source_selector_header .q-field__control {
  background-color: transparent !important;
  box-shadow: none !important;
  border-radius: 8px;
}

.transactions_import_source_selector_header .q-field__control:before,
.transactions_import_source_selector_header .q-field__control:after {
  border-color: transparent !important;
  box-shadow: none !important;
}

.modified-indicator--after {
  width: 8px;
  min-width: 8px;
  margin-left: 8px;
}

.modified-indicator::after {
  content: '';
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #5fb3d9;
  left: -12px;
  top: 50%;
  transform: translateY(-50%);
}

.modified-indicator--after::after {
  left: 0;
}

#transactions_table table tbody tr.transaction_without_changerate {
  border-bottom: none;
  opacity: 0.5;
}

#transactions_table table tbody tr.no-bottom-border {
  border-bottom: none;
}
</style>
